import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { EpAdjsLocalChartService } from './ep-adjs-local-chart.service';

interface ParentObjectModel {
  CHART_KEY: number;
  LOCAL_ACCT_KEY: number;
  ACCT_DD_DISPLAY: string;
  LOCAL_ACCT_TYPE: string;
  SCHD_H_LINE_NO: string;
  PRIOR_YEAR_AMT: number;
  CURR_YEAR_AMT: number;
  ADJ_AMT: number;
  MULTIPLIER: number;
  TP_KEY: number;
  PROJECT_ID: number;
  PROJECT_STEP_ID: number;
  AUTO_REVERSE_YN: string;
  TRANS_DETAILS_KEY: number;
  IS_ACCT_EDITABLE: string;
  POSTING_GL_ACCT_KEY: string;
}

interface OffsetRowModel {
  CHART_KEY: number;
  LOCAL_ACCT_KEY: number;
  ACCT_DD_DISPLAY: string;
  LOCAL_ACCT_TYPE: string;
  SCHD_H_LINE_NO: string;
  PRIOR_YEAR_AMT: number;
  CURR_YEAR_AMT: number;
  ADJ_AMT: number;
  MULTIPLIER: number;
  TP_KEY: number;
  PROJECT_ID: number;
  PROJECT_STEP_ID: number;
  AUTO_REVERSE_YN: string;
  TRANS_DETAILS_KEY: number;
  IS_ACCT_EDITABLE: string;
  ACCT_TP_LINE_NO: string;
  selectedSchdH?: any;
  selected?: any;
  tpObject?: any;
  tpDisplay?: string;
}

interface AdjustmentAccountModel {
  ACCTTP: string;
  LOCAL_ACCT_KEY: number;
  SYSTEM_ACCT_KEY: number;
  SYSTEM_ACCT: string;
  SYSTEM_ACCT_DESC: string;
  LOCAL_ACCT: string;
  LOCAL_ACCT_DESC: string;
  TP_ENTITY: string;
  TP_KEY: number;
  TP_REP_PD: number;
  BOOK_AMT: number | null;
  TAX_AMT: number | null;
  PERM_DIFF: number | null;
  TEMP_DIFF: number | null;
  CUR_ADJ_AMT: number | null;
  TOTAL?: number;
  TYPE: string;
  IS_DELETE: string;
  TRANS_DETAILS_KEY: number;
  ISLOADING: boolean;
  tpObject?: any;
  selected?: any;
  LINE_KEY?: number;
  ADJ_TYPE?: string;
}

interface ReportableItemModel {
  glacct: any;
  glacct_book: any;
  cy_amt: number | null;
  cy_amt_book: number | null;
  earlieradj_amt: number | null;
  earlieradj_amt_book: number | null;
  thisadj_amt: number | null;
  thisadj_amt_book: number | null;
  adj_cy_amt: number | null;
  adj_cy_amt_book: number | null;
  glacct_error: boolean;
  glacct_error_book: boolean;
  thisadj_amt_error: boolean;
  thisadj_amt_error_book: boolean;
  cy_amt_loading: boolean;
  cy_amt_book_loading: boolean;
  earlieradj_amt_loading: boolean;
  earlieradj_amt_book_loading: boolean;
  type?: string;
  old_gl_acct_key?: any;
  old_thisadj_amt?: number | null;
}

@Component({
  selector: 'gtw-ep-adjs-local-chart',
  templateUrl: './ep-adjs-local-chart.component.html',
  styleUrls: ['./ep-adjs-local-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class EpAdjsLocalChartComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // V9: Flags to prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;
  private unsubscribe$ = new Subject<void>();
  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;

  // Component properties
  title = 'Tax Adjustments';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  crudLoadingBook = false;
  showFormLoader = true;
  existingLoading = false;
  noRecords = false;
  noRecordsBook = false;
  viewOnly = false;
  showTaxAdjForm = true;
  loadingSysAcct = true;
  loadingTpAcct = true;
  loadingLines = true;
  loadingProjects = true;

  // Form data
  ADJ_DESCRIPTION = '';
  ADJ_TYPE = 'PERM';
  TRANS_HEADER_KEY: string | number = '';
  adjTotal: number = 0;
  accounts: AdjustmentAccountModel[] = [];
  existingAdjustments: any[] = [];
  delTransDetails: any[] = [];

  // FIR specific
  FIR_TRANS_TYPE = 'LOCAL_TAX_ADJ';
  SUPPL_INFO = '';
  isMaxLimitReached = false;
  lineFlag = true;
  formSubmitted = false;

  // FIR autocomplete state
  firTpSearchResults: any[] = [];
  firTpSearchText = '';
  loadingFirTp = false;
  offsetAcctSearchResults: any[][] = [];
  offsetTpSearchResults: any[][] = [];
  loadingOffsetAcct: boolean[] = [];
  loadingOffsetTp: boolean[] = [];

  codeCombination: any = null;

  // SchdM Reviewed modal
  showSchdMModal = false;
  schdMComment = { subject: '', message: '', is_public: 0, notify: [] as any[] };
  schdMUserSearch = '';
  schdMUserResults: any[] = [];
  schdMUserLoading = false;
  schdMSaving = false;

  // Parent Object for FIR
  parentObject: ParentObjectModel = {
    CHART_KEY: 0,
    LOCAL_ACCT_KEY: 0,
    ACCT_DD_DISPLAY: '',
    LOCAL_ACCT_TYPE: '',
    SCHD_H_LINE_NO: '0',
    PRIOR_YEAR_AMT: 0,
    CURR_YEAR_AMT: 0,
    ADJ_AMT: 0,
    MULTIPLIER: 1,
    TP_KEY: 0,
    PROJECT_ID: 0,
    PROJECT_STEP_ID: 0,
    AUTO_REVERSE_YN: 'N',
    TRANS_DETAILS_KEY: 0,
    IS_ACCT_EDITABLE: 'Y',
    POSTING_GL_ACCT_KEY: ''
  };

  // Offset Rows for FIR
  addOffsetRows: OffsetRowModel[] = [];
  existingOffsetAdjustments: any[] = [];
  isEdit = false;

  /** Pending FIR edit hydration when lookup lists finish loading after Edit click. */
  private pendingFirEditSync: { account: any; header: any } | null = null;
  /** Pending DCS edit row — re-hydrate reportable fields when async lists finish loading. */
  private pendingDcsEditRow: any | null = null;
  /** Saved action/type codes for re-bind after transactions/reportableTransList load. */
  private pendingReportableActionCode = '';
  private pendingReportableTypeCode = '';
  /** Pending Book reportable type hydration when reportableTransList finishes loading. */
  private pendingBookReportableType: any = null;

  compareByLocalAcctKey = (a: { LOCAL_ACCT_KEY?: number } | null, b: { LOCAL_ACCT_KEY?: number } | null): boolean =>
    !!a && !!b && a.LOCAL_ACCT_KEY === b.LOCAL_ACCT_KEY;

  compareByLineNo = (a: { LINE_NO?: string | number } | null, b: { LINE_NO?: string | number } | null): boolean =>
    !!a && !!b && String(a.LINE_NO) === String(b.LINE_NO);

  compareByProjectId = (a: { PROJECT_ID?: number } | null, b: { PROJECT_ID?: number } | null): boolean =>
    !!a && !!b && a.PROJECT_ID === b.PROJECT_ID;

  compareByProjectStepId = (a: { PROJECT_STEP_ID?: number } | null, b: { PROJECT_STEP_ID?: number } | null): boolean =>
    !!a && !!b && a.PROJECT_STEP_ID === b.PROJECT_STEP_ID;

  // Match reportable dropdown model (code string or option object) across list reloads.
  compareReportableOption = (option: any, value: any): boolean => {
    if (option == null || value == null || value === '') {
      return option === value;
    }
    const optionCode = typeof option === 'object' ? (option.CODE ?? option.CODE_NAME) : option;
    const valueCode = typeof value === 'object' ? (value.CODE ?? value.CODE_NAME) : value;
    return optionCode != null && valueCode != null && optionCode == valueCode;
  };

  isOffSetAcctExists = false;
  INVALID_OBJECT = 'N';

  // DCS specific - General
  activeScheduleTab = 0;
  reportableFlag = false;
  partnershipFlag = false;
  testFlag = false;
  postingChange = false;
  triggerLoading = false;
  triggerLoadingNew = false;

  // DCS specific - Display flags
  hideMandatoryFieldCP = true;
  hideMandatoryFieldCSchdmType = true;
  hideSaveProvY = false;
  hideCgni = false;
  custom2AmtFlag = true;

  // DCS specific - Accordion states
  mandatoryFieldsOpen = true;
  optionalFieldsOpen = true;

  // DCS specific - Header fields
  postingGL = '';
  postingTax = '';
  postingTaxTop = '';
  postingTaxTopB = '';
  // Resolved GL_ACCT_KEY for the selected posting account (New Posting Acct / testFlag variant)
  postingTaxAcctKey = '';
  postingTaxAcctKeyB = '';
  m3FormLine = '';
  m3FormLineB = '';
  bookAmt: number | null = null;
  bookAdj: number | null = null;
  taxAdj: number | null = null;
  dcsCurr = 'USD';

  // DCS specific - Form fields
  userM1 = '';
  templateKey = '';
  template = '';
  schdMt = '';
  tbbsAcctVal = '';
  CURRENT_YR_AMT: number | null = null;
  recurring = false;
  bookAmtExist = false;
  sch = '';
  userReviewd = '';
  timestamp = '';
  project = '';
  projectStep = '';
  settledYesNo = 'No';
  provision = 'No';
  COMMENTS = '';
  reportable = 'No';
  rptTFlag = 'No';
  transactionType: any = '';
  rptTaxAcct: number | null = null;
  reasonForChange = '';
  reportableTransDesc = '';
  reportableTransDescBook = '';
  taxShelterRegNum = '';
  taxShelterRegNumBook = '';
  typeOfReportableTrans: any = '';
  typeOfReportableTransBook: any = '';
  // Edit-mode original SchdM type ID (SCHDM_TYPE_ID from existing record, legacy `vm.oldschdm`) — sent directly as OLD_schdm_Type on save.
  oldSchdMId: string | number = '';
  // Edit-mode (legacy `type === true`) original reportable values for SchdM, used to build OLD_* on save.
  oldReportableAction = '';
  oldRptTaxAcct: number | null = null;
  oldReasonForChange = '';
  oldReportableTransDesc = '';
  oldTaxShelterRegNum = '';
  oldTypeOfReportableTrans = '';
  // Edit-mode original reportable values for Book Reportable (legacy `loadAdjustment_b`).
  oldReportableTransDescBook = '';
  oldTaxShelterRegNumBook = '';
  oldTypeOfReportableTransBook = '';
  refKey = 0;
  custom2Amt: number | null = null;
  private dcsSchdmId: string | number = 0;
  private oldDcsComments = '';

  // DCS specific - Trading Partner for DCS
  tpObjectD: any = null;
  tpObjectDDisplay = '';
  tpSearchResults: any[] = [];
  loadingTpsDcs = false;

  // DCS specific - Error flags
  templateError = false;
  userDescError = false;
  typeError = false;
  tbbsError = false;
  cyAmtError = false;
  schdmReviewedError = false;
  postingTaxAccountCodeError = false;
  reportableActionError = false;
  reportableTaxAmountError = false;
  rptDescError = false;
  rptDescErrorBook = false;
  taxShelterErrorBook = false;
  typeOfRptError = false;
  typeOfRptErrorBook = false;
  reasonForZeroError = false;

  // DCS specific - Loading flags
  templateLoading = false;
  schdMLoading = false;
  tbbsLoading = false;
  projectLoading = false;
  projectStepLoading = false;
  existingChartLoading = false;

  // DCS specific - Dropdown data
  schdMType: string[] = [];
  schdMTypeData: any[] = [];
  tbbsAcct: string[] = [];
  tbbsAcctData: any[] = [];
  mainModalData: any[] = [];
  tempSystemAccount: string[] = [];
  postingGLAcctM: string[] = [];
  postingGLAcctMB: string[] = [];
  // Raw "top" GL account data (GL_ACCT -> GL_ACCT_KEY) for the New Posting Acct (testFlag) variant
  glAcctsMData: any[] = [];
  glAcctsMDataB: any[] = [];
  reportableActionChangeFlag = '';
  transactions: any[] = [];
  reportableTransList: any[] = [];
  glaccts: any[] = [];
  reportableList: ReportableItemModel[] = [];
  reportableListBook: ReportableItemModel[] = [];
  existingChartData: any[] = [];
  // Raw (ungrouped) book reportable rows (legacy existing_chart_data_temp) used for book edit hydration.
  existingChartDataTemp: any[] = [];
  // Raw (ungrouped) DCS adjustment rows (legacy existingAdjustments_temp) used for SchdM reportable edit hydration.
  existingAdjustmentsTemp: any[] = [];
  // SCHDM Reportable Transaction static entry (legacy glacctsrpt / thisadj_amt1 / total_this_adj_amt)
  glacctsRpt: any[] = [];
  thisAdjAmt1: number | null = null;
  totalThisAdjAmt = 0;

  
  // BAA reportable offset row + total
  glacctsrpt: any[] = [];
  glacct1 = '5555555 - Reportable Transactions';
  cyAmt1: number | null = 0;
  earlierAdjAmt1: number | null = 0;
  adjCyAmt1: number | null = 0;
  totalThisAdjAmtStatic: number | null = 0;

  // Book reportable totals
  glacct1Book = '';
  cyAmt1Book: number | null = null;
  earlierAdjAmt1Book: number | null = null;
  thisAdjAmt1Book: number | null = null;
  adjCyAmt1Book: number | null = null;
  totalThisAdjAmtStaticBook: number | null = null;
  totalThisAdjAmtBook = 0;
  // Book reportable edit flag (legacy type_book) + aggregate validation flags.
  typeBook = false;
  glacctErrorBook = false;
  thisAdjAmtErrorBook = false;

  // Dropdown data
  localAccDropdownData: any[] = [];
  tpDropdownData: any[] = [];
  lineDetailsData: any[] = [];
  offsetLineDetailsData: any[] = [];
  adjTypesDropdown: any[] = [];
  projectsList: { list: any[], selected: any } = { list: [], selected: null };
  projectSteps: { list: any[], selected: any } = { list: [], selected: null };
  allProjectsWithSteps: any[] = [];

  // Selected values
  selectedLine: any = null;
  selectedProject: any = null;
  selectedAdjType: any = null;
  parentAcct: any = null;
  tpObject: any = null;

  // Dates
  taxYearBegin = '';
  taxYearEnd = '';

  // Screen data
  mainScreenKey: number = 0;
  gridType = '';
  sourceAcctType: number | null = null;
  transTypeActual = '';
  SOURCE_SYSTEM = '';

  // Base configuration
  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: ''
  };

  globalService: any;
  GENERAL_CONFIG: any;
  rowData: any = {};
  filterParams: any = {};

  @Input('trans-type')
  transType = 'LOCAL_TAX_ADJ_PERM';

  constructor(
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
    private epAdjsService: EpAdjsLocalChartService,
    private http: HttpClient,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => { this.resetAndTryInit(); })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.resetAndTryInit(); })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) { fp = extractObject(hydrated); }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell, globalParams: mergedGp, filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);

    // Determine the modal variant from the callback name (playbook 13.1).
    // 'epTaxAdjsLocalCharttest' = "New Posting Acct Tax Adj" column => testFlag (legacy testflag=true).
    const callBack = (activeScreen as any)?.data?.modalName
      || (activeScreen as any)?.data?.modal_name
      || (activeScreen as any)?.data?.colData?.call_back
      || '';
    this.testFlag = callBack === 'epTaxAdjsLocalCharttest';
    this.postingChange = this.testFlag;
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      this.setupInitialData();
      this.loadAccounts();
      this.loadLineDetails();
      this.loadTradingPartners();
      this.loadProjectsAndSteps();
      this.loadExistingAdjustments();

      // Load DCS specific data
      if (this.SOURCE_SYSTEM === 'DCS') {
        this.adjTypesDropdown = this.epAdjsService.getDcsAdjsTypes();
        this.loadDcsData();
      }

      // For FIR, open first offset row immediately
      if (this.SOURCE_SYSTEM === 'FIR' && this.addOffsetRows.length === 0) {
        this.addOffsetAccount();
      }

      // Load code combination for key info bar
      if (this.rowData.COMBINATION_KEY) {
        this.epAdjsService.getCodeCombination(this.GENERAL_CONFIG.base_url, this.rowData.COMBINATION_KEY)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(
            (data: any) => { this.codeCombination = data; this.cdr.detectChanges(); },
            () => { this.codeCombination = null; this.cdr.detectChanges(); }
          );
      }

      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  private setupInitialData(): void {
    this.gridType = this.rowData.GRID_TYPE || '';
    this.sourceAcctType = this.rowData.SYSTEM_ACCT_TYPE;
    this.mainScreenKey = this.filterParams.screen_key || 0;
    this.transTypeActual = this.rowData.TRANS_TYPE_ACTUAL || this.transType;
    this.SOURCE_SYSTEM = this.rowData.SOURCE_SYSTEM || 'FIR';

    // Check line flag based on TAX_TYPE
    const taxType = this.rowData.TAX_TYPE || '';
    if (['PTSP', 'BRPTSP', 'BRPTP', 'NCPTSP', 'NCPTSPNF'].includes(taxType)) {
      this.lineFlag = false;
    }

    // Set title based on source system (FIR always "Create/Edit E&P Adjustments")
    if (this.SOURCE_SYSTEM === 'DCS') {
      this.title = 'SCHDM Adjustments';
    } else {
      this.title = 'Create/Edit E&P Adjustments';
      switch (this.transTypeActual) {
        case 'LOCAL_TAX_ADJ_PERM':
          this.ADJ_TYPE = 'PERM';
          this.FIR_TRANS_TYPE = 'LOCAL_TAX_ADJ_PERM';
          break;
        case 'LOCAL_TAX_ADJ_TEMP':
          this.ADJ_TYPE = 'TEMP';
          this.FIR_TRANS_TYPE = 'LOCAL_TAX_ADJ_TEMP';
          break;
        case 'LOCAL_EP_ADJ':
          this.ADJ_TYPE = 'EP';
          this.FIR_TRANS_TYPE = 'LOCAL_TAX_ADJ';
          break;
        case 'LOCAL_TAX_ADJ':
          this.ADJ_TYPE = 'TAX';
          this.FIR_TRANS_TYPE = 'LOCAL_TAX_ADJ';
          break;
        case 'LOCAL_TAX_ADJ_GHOST':
          this.ADJ_TYPE = 'GHOST';
          this.FIR_TRANS_TYPE = 'LOCAL_TAX_ADJ_GHOST';
          break;
        default:
          break;
      }
    }

    // Format dates
    if (this.rowData.TAX_YEAR_BEGIN) {
      const beginDate = new Date(this.rowData.TAX_YEAR_BEGIN);
      this.taxYearBegin = (beginDate.getMonth() + 1) + '/' + beginDate.getDate();
    }
    if (this.rowData.TAX_YEAR_END) {
      const endDate = new Date(this.rowData.TAX_YEAR_END);
      this.taxYearEnd = (endDate.getMonth() + 1) + '/' + endDate.getDate() + '/' + endDate.getFullYear();
    }

    // Initialize DCS specific fields from rowData
    // Note: postingGL, postingTax, m3FormLine, bookAmt, bookAdj, taxAdj will be loaded via loadDcsTriggerData API call
    if (this.SOURCE_SYSTEM === 'DCS') {
      this.dcsCurr = this.rowData.CURR_CODE || 'USD';
      // These fields are typically empty in rowData and loaded via API
      // Set from rowData if available (fallback values).
      // For the New Posting Acct variant (testFlag) the posting account is chosen
      // from a dropdown, so skip the static postingGL/postingTax (legacy testflag=true).
      if (!this.testFlag && this.rowData.POSTING_GL) {
        this.postingGL = this.rowData.POSTING_GL;
      }
      if (!this.testFlag && this.rowData.POSTING_TAX) {
        this.postingTax = this.rowData.POSTING_TAX;
      }
      if (this.rowData.M3_FORM_LINE) {
        this.m3FormLine = this.rowData.M3_FORM_LINE;
      }
      if (this.rowData.BOOK_AMT !== undefined) {
        this.bookAmt = this.rowData.BOOK_AMT;
      }
      if (this.rowData.BOOK_ADJ !== undefined) {
        this.bookAdj = this.rowData.BOOK_ADJ;
      }
      if (this.rowData.TAX_ADJ !== undefined) {
        this.taxAdj = this.rowData.TAX_ADJ;
      }
    }

    const isLocalBookToTax = this.rowData.GRID_TYPE === 'LOCAL_BOOK_TO_TAX_SUMMARY';

    this.parentObject = {
      CHART_KEY: this.rowData.LOCAL_CHART_KEY || 0,
      LOCAL_ACCT_KEY: this.rowData.LOCAL_ACCT_KEY || 0,
      ACCT_DD_DISPLAY: isLocalBookToTax
        ? (this.rowData.LOCAL_ACCT + '-' + this.rowData.LOCAL_ACCT_DESC)
        : '',
      LOCAL_ACCT_TYPE: this.rowData.LOCAL_ACCT_TYPE || '',
      SCHD_H_LINE_NO: this.SOURCE_SYSTEM === 'DCS' ? '' : '0',
      PRIOR_YEAR_AMT: this.rowData.BOY_AMT || 0,
      CURR_YEAR_AMT: 0,
      ADJ_AMT: 0,
      MULTIPLIER: this.SOURCE_SYSTEM === 'DCS' ? 0 : 0,
      TP_KEY: this.rowData.TP_KEY || 0,
      PROJECT_ID: 0,
      PROJECT_STEP_ID: 0,
      AUTO_REVERSE_YN: this.SOURCE_SYSTEM === 'DCS' ? '' : 'N',
      TRANS_DETAILS_KEY: 0,
      IS_ACCT_EDITABLE: isLocalBookToTax ? 'N' : 'Y',
      POSTING_GL_ACCT_KEY: ''
    };

    // Add initial account for both FIR and DCS
    this.addAccount();
  }

  private loadDcsData(): void {
    if (this.testFlag) {
      // New Posting Acct variant: posting account is selected from a dropdown,
      // so skip loadtriggerdata (legacy testflag=true) and load the GL account options instead.
      this.triggerLoading = true;
      this.triggerLoadingNew = true;
      this.loadTopGlAccounts();
    } else {
      // Load DCS trigger data (Posting GL, Tax, M3 Form Line, Book Amt, etc.)
      this.loadDcsTriggerData();
    }

    // Load templates
    this.loadTemplates();

    // Load G/L Accounts for Book Reportable dropdowns
    this.loadGlAccounts();

    // Load Reportable Action types from API
    this.loadReportableActions();

    // Load Reportable G/L Accounts (offset row: 5555555)
    this.loadReportableGlAccounts();

    // Load Reportable Transaction Types
    this.loadReportableTransTypes();

    // Initialize reportableListBook with one empty row
    this.reportableListBook = [];
    this.addReportableBook();

    // Load existing chart data for Book Only Reportable tab
    this.loadExistingChartData();

    // Set default offset row for Book Reportable
    this.glacct1Book = '5555555 - Reportable Transactions';
    this.cyAmt1Book = 0;
    this.earlierAdjAmt1Book = 0;
    this.thisAdjAmt1Book = 0;
    this.adjCyAmt1Book = 0;
    this.totalThisAdjAmtStaticBook = 0;
  }

  private loadGlAccounts(): void {
    const params = {
      tax_year: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.filterParams.jcd_key,
      chart_key: this.rowData.LOCAL_CHART_KEY
    };

    this.epAdjsService.loadGlAccounts(this.GENERAL_CONFIG.base_url, params, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any[]) => {
          console.log('G/L Accounts loaded:', data);
          this.glaccts = data;
          this.cdr.detectChanges();
          this.tryPendingReportableHydration();
        },
        (error: any) => {
          console.error('Error loading G/L accounts:', error);
        }
      );
  }

  // New Posting Acct variant: load the "top" GL account dropdown options
  // (legacy getTopglAccts / getTopglAcctsB, action_code 'p88nfx').
  private loadTopGlAccounts(): void {
    const params = {
      tax_year: this.filterParams.tax_year || this.globalService?.globalParams?.tax_year,
      scenario: this.filterParams.scenario || this.globalService?.globalParams?.scenario,
      jcd_key: this.filterParams.jcd_key || this.globalService?.globalParams?.jcd_key,
      chart_key: this.rowData.LOCAL_CHART_KEY
    };

    this.postingTaxTop = '';
    this.postingTaxTopB = '';

    this.epAdjsService.getTopGlAccounts(this.GENERAL_CONFIG.base_url, params, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any[]) => {
          const rows = data || [];
          // Keep raw rows for GL_ACCT -> GL_ACCT_KEY lookup at save time.
          this.glAcctsMData = rows;
          this.glAcctsMDataB = rows;
          const options = Array.from(new Set(rows.map((r: any) => r.GL_ACCT)));
          this.postingGLAcctM = options;
          this.postingGLAcctMB = options;
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('Error loading top G/L accounts:', error);
        }
      );
  }

  // Resolve GL_ACCT_KEY for the currently selected posting account (testFlag variant).
  private resolvePostingAcctKey(selectedGlAcct: string, source: any[]): string {
    if (!selectedGlAcct || !source) {
      return '';
    }
    // Loose equality on purpose (GL_ACCT may be string/number from API) - playbook 5.2.
    const match = source.find((r: any) => r.GL_ACCT == selectedGlAcct);
    return match ? match.GL_ACCT_KEY : '';
  }

  // Resolve GL_ACCT_KEY from a glacct value that may be the full object (select) or a GL_ACCT string (edit hydration).
  private resolveGlAcctKey(glacct: any): any {
    if (glacct && typeof glacct === 'object') {
      return glacct.GL_ACCT_KEY;
    }
    const match = this.glaccts.find((g: any) => g.GL_ACCT === glacct);
    return match ? match.GL_ACCT_KEY : undefined;
  }

  // Resolve a glacct dropdown object from GL_ACCT_KEY, GL_ACCT string, or an existing select value.
  private resolveGlAcctObject(glacctOrKey: any): any | null {
    if (glacctOrKey == null || glacctOrKey === '') {
      return null;
    }
    if (typeof glacctOrKey === 'object') {
      return glacctOrKey.GL_ACCT_KEY != null ? glacctOrKey : null;
    }
    const list = this.glaccts || [];
    const byKey = list.find((g: any) => g.GL_ACCT_KEY == glacctOrKey);
    if (byKey) {
      return byKey;
    }
    return list.find((g: any) => g.GL_ACCT === glacctOrKey) ?? null;
  }

  private getReportableRtAcctKey(): any {
    const rpt = this.glacctsrpt?.length ? this.glacctsrpt : this.glacctsRpt;
    return rpt?.[0]?.RT_ACCT_KEY;
  }

  private isReportableTransactionsSummaryRow(raw: any, glAcctKey: any): boolean {
    if (raw.GL_ACCT === '5555555 - Reportable Transactions') {
      return true;
    }
    const rtAcctKey = this.getReportableRtAcctKey();
    return rtAcctKey != null && glAcctKey == rtAcctKey;
  }

  private mergeReportableRowFields(target: any, source: any): void {
    const fields = [
      'REPO_TRANS', 'REPO_ACTION', 'REPO_ACTION_CODE', 'REPO_TAX_AMT', 'REP_TAX_AMT',
      'REPORTABLE_TAX_AMOUNT', 'REPO_TRANS_DESC', 'REASON_BOOK_AMT', 'DR8A_LINE_NO',
      'TYPE_REPO_TRNS', 'TYPE_REPO_TRNS_CODE', 'REPORTABLE_ACTION', 'REPORTABLE_ACTION_CODE',
      'NEW_REPO_TRANS', 'OLD_REPO_TRANS',
      'NEW_REPORTABLE_ACTION', 'OLD_REPORTABLE_ACTION',
      'NEW_REPORTABLE_TAX_AMOUNT', 'OLD_REPORTABLE_TAX_AMOUNT',
      'NEW_TYPE_REPORTABLE_TRANSACTION', 'OLD_TYPE_REPORTABLE_TRANSACTION',
      'NEW_REPORTABLE_TRANSACTION_DESCRIPTION', 'OLD_REPORTABLE_TRANSACTION_DESCRIPTION',
      'NEW_REASON_FOR_ZERO_BOOK_AMT', 'OLD_REASON_FOR_ZERO_BOOK_AMT',
      'NEW_TAX_SHELTER_REGISTRATION_NUMBER', 'OLD_TAX_SHELTER_REGISTRATION_NUMBER',
      'NEW_GL_ACCT', 'OLD_GL_ACCT', 'NEW_THIS_ADJ_AMT', 'OLD_THIS_ADJ_AMT',
      'GL_ACCT_KEY', 'GL_ACCT', 'ADJ_AMOUNT',
      'ADJ_SOURCE'
    ];
    for (const field of fields) {
      if (source[field] != null && source[field] !== '') {
        target[field] = source[field];
      }
    }
  }

  private finalizeReportableAliases(row: any): any {
    row.REPO_TRANS = row.REPO_TRANS ?? row.NEW_REPO_TRANS ?? row.OLD_REPO_TRANS;
    row.REPO_ACTION = row.REPO_ACTION ?? row.REPORTABLE_ACTION;
    row.REPO_ACTION_CODE = row.REPO_ACTION_CODE
      ?? row.REPORTABLE_ACTION_CODE
      ?? row.NEW_REPORTABLE_ACTION
      ?? row.OLD_REPORTABLE_ACTION
      ?? row.ADJ_SOURCE;
    row.REPO_TAX_AMT = row.REPO_TAX_AMT
      ?? row.REP_TAX_AMT
      ?? row.REPORTABLE_TAX_AMOUNT
      ?? row.NEW_REPORTABLE_TAX_AMOUNT
      ?? row.OLD_REPORTABLE_TAX_AMOUNT;
    row.TYPE_REPO_TRNS_CODE = row.TYPE_REPO_TRNS_CODE
      ?? row.NEW_TYPE_REPORTABLE_TRANSACTION
      ?? row.OLD_TYPE_REPORTABLE_TRANSACTION;
    row.REPO_TRANS_DESC = row.REPO_TRANS_DESC
      ?? row.NEW_REPORTABLE_TRANSACTION_DESCRIPTION
      ?? row.OLD_REPORTABLE_TRANSACTION_DESCRIPTION;
    row.REASON_BOOK_AMT = row.REASON_BOOK_AMT
      ?? row.NEW_REASON_FOR_ZERO_BOOK_AMT
      ?? row.OLD_REASON_FOR_ZERO_BOOK_AMT;
    row.DR8A_LINE_NO = row.DR8A_LINE_NO
      ?? row.NEW_TAX_SHELTER_REGISTRATION_NUMBER
      ?? row.OLD_TAX_SHELTER_REGISTRATION_NUMBER;
    return row;
  }

  private buildMergedSchdmRow(data: any[], schdmId: any, baseRow: any): any {
    const merged = { ...baseRow };
    const refKey = baseRow.REFERENCE_KEY;
    for (const row of data) {
      const matchesSchdm = schdmId != null && row.SCHDM_ID == schdmId;
      // jsonSettings rows often share REFERENCE_KEY but may omit SCHDM_ID.
      const matchesRef = refKey != null && row.REFERENCE_KEY == refKey;
      if (matchesSchdm || matchesRef) {
        this.mergeReportableRowFields(merged, row);
      }
    }
    return this.finalizeReportableAliases(merged);
  }

  // Resolve the reportable transaction type code from an object (select) or a code / CODE_DESC string.
  private resolveTypeReportableBookCode(value: any): string {
    return this.resolveTypeReportableTransCode(value);
  }

  private resolveTypeReportableTransCode(value: any): string {
    if (!value) {
      return '';
    }
    if (typeof value === 'object') {
      return value.CODE || value.CODE_NAME || '';
    }
    const list = this.reportableTransList || [];
    const byDesc = list.find((t: any) => t.CODE_DESC === value);
    if (byDesc) {
      return byDesc.CODE || byDesc.CODE_NAME || '';
    }
    const byCode = list.find((t: any) => t.CODE == value || t.CODE_NAME == value);
    return byCode ? (byCode.CODE || byCode.CODE_NAME) : (value || '');
  }

  private resolveReportableActionCode(value: any): string {
    if (!value) {
      return '';
    }
    if (typeof value === 'object') {
      return value.CODE || value.CODE_NAME || '';
    }
    const list = this.transactions || [];
    const byDesc = list.find((t: any) => t.CODE_DESC === value);
    if (byDesc) {
      return byDesc.CODE || byDesc.CODE_NAME || '';
    }
    const byCode = list.find((t: any) => t.CODE == value || t.CODE_NAME == value);
    return byCode ? (byCode.CODE || byCode.CODE_NAME) : (value || '');
  }

  private findReportableActionOption(code: any): any {
    if (code == null || code === '') {
      return null;
    }
    if (typeof code === 'object') {
      return code;
    }
    return (this.transactions || []).find((t: any) => t.CODE == code || t.CODE_NAME == code) ?? code;
  }

  private findReportableTypeOption(code: any): any {
    if (code == null || code === '') {
      return null;
    }
    if (typeof code === 'object') {
      return code;
    }
    return (this.reportableTransList || []).find((t: any) => t.CODE == code || t.CODE_NAME == code) ?? code;
  }

  private applyReportableSelectValues(actionCode: string, typeCode: string): void {
    if (actionCode) {
      this.transactionType = this.findReportableActionOption(actionCode);
    }
    if (typeCode) {
      this.typeOfReportableTrans = this.findReportableTypeOption(typeCode);
    }
    this.reportableActionChangeFlag = this.normalizeReportableActionCode(this.transactionType)
      || this.normalizeReportableActionCode(actionCode);
    this.cdr.detectChanges();
  }

  private reapplyStoredReportableSelects(): void {
    if (!this.isEdit || !this.pendingDcsEditRow) {
      return;
    }
    if (!this.pendingReportableActionCode && !this.pendingReportableTypeCode) {
      return;
    }
    this.applyReportableSelectValues(
      this.pendingReportableActionCode,
      this.pendingReportableTypeCode
    );
    setTimeout(() => {
      this.applyReportableSelectValues(
        this.pendingReportableActionCode,
        this.pendingReportableTypeCode
      );
    }, 0);
  }

  private normalizeReportableActionCode(value: any): string {
    const resolved = this.resolveReportableActionCode(value);
    return ['BAA', 'ZBA', 'DP'].includes(resolved) ? resolved : '';
  }

  private extractReportableTaxAmount(row: any): number | null {
    const val = row.REPO_TAX_AMT
      ?? row.REP_TAX_AMT
      ?? row.REPORTABLE_TAX_AMOUNT
      ?? row.NEW_REPORTABLE_TAX_AMOUNT
      ?? row.OLD_REPORTABLE_TAX_AMOUNT;
    if (val == null || val === '') {
      return null;
    }
    const num = Number(val);
    return isNaN(num) ? null : num;
  }

  // Pull reportable scalars from raw rows when the grouped header row is incomplete.
  private getReportableSourceRow(data: any): any {
    return this.buildMergedSchdmRow(this.existingAdjustmentsTemp, data.SCHDM_ID, data);
  }

  private hydrateDcsReportableFields(data: any): void {
    const source = this.getReportableSourceRow(data);

    this.rptTFlag = source.REPO_TRANS === 'Y' ? 'Yes' : 'No';
    this.reportable = this.rptTFlag;
    this.rptTaxAcct = this.extractReportableTaxAmount(source);
    this.reasonForChange = source.REASON_BOOK_AMT;
    this.reportableTransDesc = source.REPO_TRANS_DESC;
    this.taxShelterRegNum = source.DR8A_LINE_NO;

    const actionCode = this.resolveReportableActionCode(source.REPO_ACTION_CODE)
      || this.resolveReportableActionCode(source.REPO_ACTION);
    const typeCode = this.resolveTypeReportableTransCode(source.TYPE_REPO_TRNS_CODE)
      || this.resolveTypeReportableTransCode(source.TYPE_REPO_TRNS);

    this.pendingReportableActionCode = actionCode;
    this.pendingReportableTypeCode = typeCode;
    this.applyReportableSelectValues(actionCode, typeCode);

    this.oldReportableAction = source.REPO_ACTION_CODE;
    this.oldRptTaxAcct = this.rptTaxAcct;
    this.oldReasonForChange = source.REASON_BOOK_AMT;
    this.oldReportableTransDesc = source.REPO_TRANS_DESC;
    this.oldTaxShelterRegNum = source.DR8A_LINE_NO;
    this.oldTypeOfReportableTrans = source.TYPE_REPO_TRNS_CODE;
  }

  // Legacy edit hydration 576-613: rebuild BAA G/L rows from raw saved rows for this REF_KEY.
  private rebuildReportableListFromExisting(): void {
    this.reportableList = [];
    if (this.reportableActionChangeFlag !== 'BAA' || !this.refKey) {
      return;
    }

    const seenGlAcctKeys = new Set<any>();

    for (const raw of this.existingAdjustmentsTemp) {
      // Loose equality on purpose (REFERENCE_KEY may be string/number) - playbook 5.2.
      if (raw.REFERENCE_KEY != this.refKey) {
        continue;
      }

      const glAcctKey = raw.NEW_GL_ACCT
        ?? raw.OLD_GL_ACCT
        ?? raw.GL_ACCT_KEY
        ?? this.resolveGlAcctKey(raw.GL_ACCT);
      if (glAcctKey == null || glAcctKey === '') {
        continue;
      }
      if (this.isReportableTransactionsSummaryRow(raw, glAcctKey)) {
        continue;
      }
      if (seenGlAcctKeys.has(glAcctKey)) {
        continue;
      }
      seenGlAcctKeys.add(glAcctKey);

      const glacctObj = this.resolveGlAcctObject(glAcctKey)
        ?? (raw.GL_ACCT ? this.resolveGlAcctObject(raw.GL_ACCT) : null);

      const thisAdjAmt = raw.NEW_THIS_ADJ_AMT ?? raw.OLD_THIS_ADJ_AMT ?? raw.ADJ_AMOUNT ?? 0;
      const oldThisAdjAmt = raw.OLD_THIS_ADJ_AMT ?? raw.ADJ_AMOUNT ?? thisAdjAmt;
      const oldGlAcctKey = raw.OLD_GL_ACCT ?? raw.GL_ACCT_KEY ?? glAcctKey;

      this.reportableList.push({
        glacct: glacctObj,
        glacct_book: null,
        old_gl_acct_key: oldGlAcctKey,
        old_thisadj_amt: oldThisAdjAmt,
        cy_amt: 0,
        cy_amt_book: null,
        earlieradj_amt: 0,
        earlieradj_amt_book: null,
        thisadj_amt: thisAdjAmt,
        thisadj_amt_book: null,
        adj_cy_amt: 0,
        adj_cy_amt_book: null,
        glacct_error: false,
        glacct_error_book: false,
        thisadj_amt_error: false,
        thisadj_amt_error_book: false,
        cy_amt_loading: false,
        cy_amt_book_loading: false,
        earlieradj_amt_loading: false,
        earlieradj_amt_book_loading: false,
        type: 'E'
      });
    }

    this.adjustedAmtChange();
    for (const row of this.reportableList) {
      row.adj_cy_amt = (row.cy_amt || 0) + (row.earlieradj_amt || 0) + (row.thisadj_amt || 0);
    }
  }

  private tryPendingReportableHydration(): void {
    if (this.pendingDcsEditRow && this.isEdit && this.SOURCE_SYSTEM === 'DCS') {
      this.reapplyStoredReportableSelects();
      this.rebuildReportableListFromExisting();
    }

    if (this.pendingBookReportableType != null && this.typeBook) {
      const resolved = this.resolveTypeReportableTransCode(this.pendingBookReportableType);
      if (resolved) {
        this.typeOfReportableTransBook = this.findReportableTypeOption(resolved);
        this.pendingBookReportableType = null;
      }
    }

    this.cdr.detectChanges();
  }

  private loadReportableGlAccounts(): void {
    this.epAdjsService.loadReportableGlAccounts(
      this.GENERAL_CONFIG.base_url,
      this.rowData.LOCAL_CHART_KEY,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        this.glacctsrpt = data;
        this.glacctsRpt = data;
        if (data && data.length > 0) {
          this.glacct1 = data[0].RT_ACCT;
          this.glacct1Book = data[0].RT_ACCT;
        }
        this.tryPendingReportableHydration();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading reportable G/L accounts:', error);
      }
    );
  }

  private loadReportableActions(): void {
    this.epAdjsService.loadReportableActions(this.GENERAL_CONFIG.base_url, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any[]) => {
          this.transactions = data;
          this.cdr.detectChanges();
          this.tryPendingReportableHydration();
        },
        (error: any) => {
          console.error('Error loading reportable actions, using fallback:', error);
          this.transactions = this.epAdjsService.getReportableActionsStatic();
          this.cdr.detectChanges();
          this.tryPendingReportableHydration();
        }
      );
  }

  private loadReportableTransTypes(): void {
    const params = {
      tax_year: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.filterParams.jcd_key
    };

    this.epAdjsService.loadReportableTransTypes(this.GENERAL_CONFIG.base_url, params, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any[]) => {
          console.log('Reportable Trans Types loaded:', data);
          this.reportableTransList = data;
          this.cdr.detectChanges();
          this.tryPendingReportableHydration();
        },
        (error: any) => {
          console.error('Error loading reportable trans types:', error);
        }
      );
  }

  private loadDcsTriggerData(): void {
    // Check if we already have data from rowData
    if (this.postingGL && this.postingTax && this.m3FormLine) {
      this.triggerLoading = true;
      this.triggerLoadingNew = true;
      this.cdr.detectChanges();
      return;
    }

    this.triggerLoading = false;
    this.triggerLoadingNew = false;

    this.epAdjsService.loadDcsTriggerData(
      this.GENERAL_CONFIG.base_url,
      this.rowData.COMBINATION_KEY,
      this.rowData.LOCAL_ACCT_KEY,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        this.triggerLoading = true;
        this.triggerLoadingNew = true;
        if (data && data.length > 0) {
          this.postingGL = data[0].POSTING_ACCT || '';
          this.postingTax = data[0].POSTING_TAC || '';
          this.m3FormLine = data[0].M3_ACCT || '';
          this.bookAmt = data[0].BOOK_AMT || 0;
          this.bookAdj = data[0].BOOK_ADJ || 0;
          this.taxAdj = data[0].TAX_ADJ || 0;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading DCS trigger data:', error);
        this.triggerLoading = true;
        this.triggerLoadingNew = true;
        this.cdr.detectChanges();
      }
    );
  }

  private loadTemplates(): void {
    // Mirror legacy `loadtemplatedata` (EpAdjustmentsLocalController.js ~line 2704):
    //   - reset mainmodaldata/tempsystemaccount
    //   - flip templateLoading on, populate Template dropdown options from API
    //     (SCHDM_TEMPLATE column, de-duplicated), then flip loading off.
    //   - DO NOT auto-select a template or cascade. Legacy leaves the Template
    //     dropdown unselected on modal open; the user picks a value which then
    //     triggers `ctrl.changeTemplate()` and the schdm/tbbs cascade.
    // The edit path (`loadAdjustment`) preserves the saved template/schdm/tbbs
    // values and explicitly invokes `loadSchdMType()` so dropdown options are
    // hydrated alongside the saved selection.
    this.templateLoading = true;
    this.tempSystemAccount = [];
    this.mainModalData = [];

    this.epAdjsService.loadTemplateData(
      this.GENERAL_CONFIG.base_url,
      this.rowData.LOCAL_CHART_KEY,
      this.rowData.LOCAL_ACCT_KEY,
      this.rowData.SYSTEM_ACCT_KEY,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        this.mainModalData = data || [];
        const templateNames = (data || []).map(item => item.SCHDM_TEMPLATE);
        this.tempSystemAccount = [...new Set(templateNames)];
        this.templateLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading templates:', error);
        this.templateLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  addAccount(): void {
    const newAcct: AdjustmentAccountModel = {
      ACCTTP: '',
      LOCAL_ACCT_KEY: 0,
      SYSTEM_ACCT_KEY: 0,
      SYSTEM_ACCT: '',
      SYSTEM_ACCT_DESC: '',
      LOCAL_ACCT: '',
      LOCAL_ACCT_DESC: '',
      TP_ENTITY: '',
      TP_KEY: 0,
      TP_REP_PD: 0,
      BOOK_AMT: null,
      TAX_AMT: null,
      PERM_DIFF: null,
      TEMP_DIFF: null,
      CUR_ADJ_AMT: null,
      TYPE: 'blank',
      IS_DELETE: 'NO',
      TRANS_DETAILS_KEY: 0,
      ISLOADING: false
    };
    this.accounts.push(newAcct);
    this.cdr.detectChanges();
  }

  removeAccount(index: number): void {
    const account = this.accounts[index];
    if (account.TYPE === 'edit') {
      account.IS_DELETE = 'YES';
      this.delTransDetails.push(account);
    }
    this.accounts.splice(index, 1);
    this.setAdjTotal();
    this.cdr.detectChanges();
  }

  private loadAccounts(): void {
    this.loadingSysAcct = true;
    const balSheetAccts = [1, 2, 3];
    const acctType = this.sourceAcctType || 0;
    const acctTypeStr = balSheetAccts.includes(acctType) ? '1,2,3' : '5,6,7';

    const params = {
      screen_key: this.mainScreenKey,
      ge_gecs_entity: this.rowData.GE_GECS_ENTITY || '',
      source_type: this.rowData.SOURCE_SYSTEM === 'DCS' ? 'D' : 'F',
      search_string: '',
      acct_type_str: acctTypeStr,
      chart_key: this.rowData.LOCAL_CHART_KEY,
      parent_acct_key: this.rowData.LOCAL_ACCT_KEY || 0,
      system_acct_search: 'N',
      parent_system_acct_key: this.rowData.SYSTEM_ACCT_KEY || 0
    };

    this.epAdjsService.getAccounts(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        this.localAccDropdownData = data;
        this.loadingSysAcct = false;
        this.showFormLoader = false;
        this.tryPendingFirEditSync();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading accounts:', error);
        this.loadingSysAcct = false;
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadLineDetails(): void {
    this.loadingLines = true;
    const taxType = this.rowData.TAX_TYPE || '';

    this.epAdjsService.getLineDetails(
      this.GENERAL_CONFIG.base_url,
      this.mainScreenKey,
      taxType,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        this.lineDetailsData = data;
        this.loadingLines = false;
        this.tryPendingFirEditSync();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading line details:', error);
        this.loadingLines = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadTradingPartners(): void {
    this.loadingTpAcct = true;

    this.epAdjsService.getTradingPartners(
      this.GENERAL_CONFIG.base_url,
      this.filterParams.tax_year || this.rowData.TAX_YEAR,
      this.filterParams.scenario || this.rowData.SCENARIO,
      '',
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        this.tpDropdownData = data;
        this.loadingTpAcct = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading trading partners:', error);
        this.loadingTpAcct = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadProjectsAndSteps(): void {
    this.loadingProjects = true;
    this.projectLoading = true;

    this.epAdjsService.getProjectsAndSteps(
      this.GENERAL_CONFIG.base_url,
      this.filterParams.tax_year || this.rowData.TAX_YEAR,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        this.allProjectsWithSteps = data;
        const projects = data.filter((item: any, index: number, self: any[]) =>
          index === self.findIndex((t: any) => t.PROJECT_ID === item.PROJECT_ID)
        );
        this.projectsList.list = projects;
        this.loadingProjects = false;
        this.projectLoading = false;
        this.tryPendingFirEditSync();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading projects:', error);
        this.loadingProjects = false;
        this.projectLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  /** API may return RECUR_FLAG on header/account while save uses AUTO_REVERSE_YN. */
  private resolveAutoReverseYn(account: any, header: any): string {
    const raw =
      account?.AUTO_REVERSE_YN ??
      header?.AUTO_REVERSE_YN ??
      account?.RECUR_FLAG ??
      header?.RECUR_FLAG;
    if (raw === true || raw === 1 || raw === '1') {
      return 'Y';
    }
    if (raw === false || raw === 0 || raw === '0') {
      return 'N';
    }
    const normalized = String(raw ?? 'N').trim().toUpperCase();
    return normalized === 'Y' ? 'Y' : 'N';
  }

  private buildFirTpEntityFromAccount(account: any): string {
    if (!account?.TP_KEY) {
      return '';
    }
    if (account.TP_ENTITY) {
      return account.TP_ENTITY;
    }
    if (account.TP_ME) {
      return `${account.TP_LEID}-${account.TP_CDR_NO}-${account.TP_REP_PD}-${account.TP_ME}`;
    }
    return `${account.TP_LEID}-${account.TP_CDR_NO}-${account.TP_REP_PD}`;
  }

  private syncFirEpAdjFormForEdit(account: any, header: any): void {
    if (!account) {
      return;
    }

    for (const prop of Object.keys(this.parentObject)) {
      if (Object.prototype.hasOwnProperty.call(account, prop)) {
        (this.parentObject as any)[prop] = account[prop];
      }
    }

    this.SUPPL_INFO = header?.SUPPL_INFO ?? '';
    this.parentObject.AUTO_REVERSE_YN = this.resolveAutoReverseYn(account, header);

    let needsRetry = false;

    if (account.LOCAL_ACCT_KEY) {
      const matchedAcct = this.localAccDropdownData.find(
        (a: any) => a.LOCAL_ACCT_KEY === account.LOCAL_ACCT_KEY
      );
      if (matchedAcct) {
        this.setParentAccount(matchedAcct);
      } else {
        if (!this.localAccDropdownData.length) {
          needsRetry = true;
        }
        this.parentAcct = null;
        if (account.ACCT_DD_DISPLAY || account.LOCAL_ACCT) {
          this.parentObject.ACCT_DD_DISPLAY =
            account.ACCT_DD_DISPLAY || `${account.LOCAL_ACCT} - ${account.LOCAL_ACCT_DESC || ''}`;
        }
      }
    }

    const lineNo = this.parentObject.SCHD_H_LINE_NO;
    if (lineNo !== undefined && lineNo !== null && lineNo !== '') {
      if (this.lineDetailsData.length) {
        const matchedLine = this.lineDetailsData.find(
          (l: any) => String(l.LINE_NO) === String(lineNo)
        );
        if (matchedLine) {
          this.changeSchd(matchedLine);
        } else {
          this.selectedLine = null;
        }
      } else {
        needsRetry = true;
      }
    }

    this.projectsList.selected = null;
    this.projectSteps.selected = null;
    if (this.parentObject.PROJECT_ID && this.parentObject.PROJECT_ID !== 0) {
      if (this.projectsList.list.length) {
        const matchedProject = this.projectsList.list.find(
          (p: any) => p.PROJECT_ID === this.parentObject.PROJECT_ID
        );
        if (matchedProject) {
          this.projectsList.selected = matchedProject;
          this.changeProject();
          const matchedStep = this.projectSteps.list.find(
            (s: any) => s.PROJECT_STEP_ID === this.parentObject.PROJECT_STEP_ID
          );
          if (matchedStep) {
            this.projectSteps.selected = matchedStep;
            this.changeProjectStep();
          }
        }
      } else {
        needsRetry = true;
      }
    }

    if (account.TP_KEY && account.TP_KEY !== 0) {
      const tpEntity = this.buildFirTpEntityFromAccount(account);
      this.firTpSearchText = tpEntity;
      this.parentObject.TP_KEY = account.TP_KEY;
      this.tpObject = {
        TP_ENTITY: tpEntity,
        TP_KEY: account.TP_KEY,
        TP_REP_PD: account.TP_REP_PD,
        TP_LEID: account.TP_LEID,
        TP_CDR_NO: account.TP_CDR_NO,
        TP_ME: account.TP_ME
      };
    } else {
      this.firTpSearchText = '';
      this.tpObject = null;
      this.parentObject.TP_KEY = 0;
    }

    this.pendingFirEditSync = needsRetry ? { account, header } : null;
    this.cdr.detectChanges();
  }

  private tryPendingFirEditSync(): void {
    if (!this.pendingFirEditSync || !this.isEdit || this.SOURCE_SYSTEM === 'DCS') {
      return;
    }
    this.syncFirEpAdjFormForEdit(this.pendingFirEditSync.account, this.pendingFirEditSync.header);
  }

  // FIR specific methods
  setParentAccount(account: any): void {
    if (!account) return;
    this.parentObject.LOCAL_ACCT_KEY = account.LOCAL_ACCT_KEY;
    this.parentObject.LOCAL_ACCT_TYPE = account.LOCAL_ACCT_TYPE;
    this.parentObject.ACCT_DD_DISPLAY = account.ACCT_DD_DISPLAY || `${account.LOCAL_ACCT} - ${account.LOCAL_ACCT_DESC}`;
    this.parentAcct = account;
    this.cdr.detectChanges();
  }

  setParentTradingPartner(tp: any): void {
    if (!tp) return;
    this.parentObject.TP_KEY = tp.TP_KEY;
    this.tpObject = tp;
    this.cdr.detectChanges();
  }

  changeSchd(line: any): void {
    if (line) {
      this.parentObject.SCHD_H_LINE_NO = line.LINE_NO;
      this.selectedLine = line;
    }
    this.cdr.detectChanges();
  }

  changeProject(): void {
    if (this.projectsList.selected) {
      const stepsExists = this.projectsList.selected.STEPS_EXISTS;
      const unsortedProjectSteps = this.allProjectsWithSteps.filter(
        (item: any) => item.PROJECT_ID === this.projectsList.selected.PROJECT_ID
      );

      if (stepsExists > 0) {
        this.projectSteps.list = unsortedProjectSteps.sort(
          (a: any, b: any) => a.STEP_NUMBER - b.STEP_NUMBER
        );
      } else {
        this.projectSteps.list = [{ STEP_SHORT_DESCRIPTION: 'No Steps Found' }];
      }

      this.parentObject.PROJECT_ID = this.projectsList.selected.PROJECT_ID;
    } else {
      this.projectSteps.list = [];
      this.projectSteps.selected = null;
      this.parentObject.PROJECT_ID = 0;
      this.parentObject.PROJECT_STEP_ID = 0;
    }
    this.cdr.detectChanges();
  }

  changeProjectStep(): void {
    if (this.projectSteps.selected) {
      this.parentObject.PROJECT_STEP_ID = this.projectSteps.selected.PROJECT_STEP_ID;
    } else {
      this.parentObject.PROJECT_STEP_ID = 0;
    }
    this.cdr.detectChanges();
  }

  addOffsetAccount(): void {
    const offsetObject: OffsetRowModel = {
      CHART_KEY: this.rowData.LOCAL_CHART_KEY,
      LOCAL_ACCT_KEY: 0,
      ACCT_DD_DISPLAY: '',
      LOCAL_ACCT_TYPE: '',
      SCHD_H_LINE_NO: '0',
      PRIOR_YEAR_AMT: 0,
      CURR_YEAR_AMT: 0,
      ADJ_AMT: 0,
      MULTIPLIER: 0,
      TP_KEY: 0,
      PROJECT_ID: 0,
      PROJECT_STEP_ID: 0,
      AUTO_REVERSE_YN: 'N',
      TRANS_DETAILS_KEY: 0,
      IS_ACCT_EDITABLE: 'Y',
      ACCT_TP_LINE_NO: '',
      selectedSchdH: this.offsetLineDetailsData.length > 0 ? this.offsetLineDetailsData[0] : null
    };
    this.addOffsetRows.push(offsetObject);
    this.cdr.detectChanges();
  }

  removeOffsetAccount(index: number): void {
    this.addOffsetRows.splice(index, 1);
    this.cdr.detectChanges();
  }

  setOffsetAccount(account: any, index: number): void {
    if (!account) return;
    this.addOffsetRows[index].LOCAL_ACCT_KEY = account.LOCAL_ACCT_KEY;
    this.addOffsetRows[index].LOCAL_ACCT_TYPE = account.LOCAL_ACCT_TYPE;
    this.addOffsetRows[index].ACCT_DD_DISPLAY = account.ACCT_DD_DISPLAY || `${account.LOCAL_ACCT} - ${account.LOCAL_ACCT_DESC}`;
    this.addOffsetRows[index].ACCT_TP_LINE_NO = `${account.LOCAL_ACCT_KEY}${this.addOffsetRows[index].TP_KEY}${this.addOffsetRows[index].SCHD_H_LINE_NO}`;
    this.isOffSetAcctExists = true;
    this.INVALID_OBJECT = 'N';
    this.cdr.detectChanges();
  }

  setOffsetTradingPartner(tp: any, index: number): void {
    if (!tp) return;
    this.addOffsetRows[index].TP_KEY = tp.TP_KEY;
    this.addOffsetRows[index].ACCT_TP_LINE_NO = `${this.addOffsetRows[index].LOCAL_ACCT_KEY}${tp.TP_KEY}${this.addOffsetRows[index].SCHD_H_LINE_NO}`;
    this.INVALID_OBJECT = 'N';
    this.cdr.detectChanges();
  }

  // FIR Trading Partner autocomplete
  searchFirTradingPartners(event: any): void {
    const val = (event.target as HTMLInputElement).value;
    this.firTpSearchText = val;
    if (!val || val.length < 3) {
      this.firTpSearchResults = [];
      this.cdr.detectChanges();
      return;
    }
    this.loadingFirTp = true;
    const taxYear = this.filterParams.tax_year || this.globalService?.globalParams?.tax_year;
    const scenario = this.filterParams.scenario || this.globalService?.globalParams?.scenario;
    this.epAdjsService.getTradingPartners(
      this.GENERAL_CONFIG.base_url, taxYear, scenario, val, this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => { this.firTpSearchResults = data || []; this.loadingFirTp = false; this.cdr.detectChanges(); },
      () => { this.firTpSearchResults = []; this.loadingFirTp = false; this.cdr.detectChanges(); }
    );
  }

  selectFirTradingPartner(tp: any): void {
    this.tpObject = tp;
    this.parentObject.TP_KEY = tp.TP_KEY;
    this.firTpSearchText = tp.TP_ENTITY;
    this.firTpSearchResults = [];
    this.cdr.detectChanges();
  }

  // Offset Account autocomplete
  searchOffsetAccounts(event: any, index: number): void {
    const val = (event.target as HTMLInputElement).value;
    if (!val || val.length < 3) {
      this.offsetAcctSearchResults[index] = [];
      this.cdr.detectChanges();
      return;
    }
    this.loadingOffsetAcct[index] = true;
    this.epAdjsService.getAccounts(
      this.GENERAL_CONFIG.base_url,
      {
        screen_key: this.mainScreenKey,
        ge_gecs_entity: this.rowData.GE_GECS_ENTITY || '',
        source_type: this.rowData.SOURCE_SYSTEM === 'DCS' ? 'D' : 'F',
        search_string: val,
        acct_type_str: '1,2,3',
        chart_key: this.rowData.LOCAL_CHART_KEY,
        parent_acct_key: this.parentObject.LOCAL_ACCT_KEY,
        system_acct_search: '',
        parent_system_acct_key: this.rowData.SYSTEM_ACCT_KEY || 0
      },
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => { this.offsetAcctSearchResults[index] = data || []; this.loadingOffsetAcct[index] = false; this.cdr.detectChanges(); },
      () => { this.offsetAcctSearchResults[index] = []; this.loadingOffsetAcct[index] = false; this.cdr.detectChanges(); }
    );
  }

  selectOffsetAccount(account: any, index: number): void {
    this.addOffsetRows[index].LOCAL_ACCT_KEY = account.LOCAL_ACCT_KEY;
    this.addOffsetRows[index].LOCAL_ACCT_TYPE = account.LOCAL_ACCT_TYPE;
    this.addOffsetRows[index].ACCT_DD_DISPLAY = account.ACCT_DD_DISPLAY || `${account.LOCAL_ACCT} - ${account.LOCAL_ACCT_DESC}`;
    this.addOffsetRows[index].ACCT_TP_LINE_NO = `${account.LOCAL_ACCT_KEY}${this.addOffsetRows[index].TP_KEY}${this.addOffsetRows[index].SCHD_H_LINE_NO}`;
    this.isOffSetAcctExists = true;
    this.INVALID_OBJECT = 'N';
    this.offsetAcctSearchResults[index] = [];
    this.cdr.detectChanges();
  }

  searchOffsetTradingPartners(event: any, index: number): void {
    const val = (event.target as HTMLInputElement).value;
    if (!val || val.length < 3) {
      this.offsetTpSearchResults[index] = [];
      this.cdr.detectChanges();
      return;
    }
    this.loadingOffsetTp[index] = true;
    const taxYear = this.filterParams.tax_year || this.globalService?.globalParams?.tax_year;
    const scenario = this.filterParams.scenario || this.globalService?.globalParams?.scenario;
    this.epAdjsService.getTradingPartners(
      this.GENERAL_CONFIG.base_url, taxYear, scenario, val, this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => { this.offsetTpSearchResults[index] = data || []; this.loadingOffsetTp[index] = false; this.cdr.detectChanges(); },
      () => { this.offsetTpSearchResults[index] = []; this.loadingOffsetTp[index] = false; this.cdr.detectChanges(); }
    );
  }

  selectOffsetTradingPartner(tp: any, index: number): void {
    if (!tp) return;
    this.addOffsetRows[index].TP_KEY = tp.TP_KEY;
    this.addOffsetRows[index].tpDisplay = tp.TP_ENTITY;
    this.addOffsetRows[index].ACCT_TP_LINE_NO = `${this.addOffsetRows[index].LOCAL_ACCT_KEY}${tp.TP_KEY}${this.addOffsetRows[index].SCHD_H_LINE_NO}`;
    this.INVALID_OBJECT = 'N';
    this.offsetTpSearchResults[index] = [];
    this.cdr.detectChanges();
  }

  changeOffsetSchd(index: number, line: any): void {
    if (line) {
      this.addOffsetRows[index].SCHD_H_LINE_NO = line.LINE_NO;
      this.addOffsetRows[index].ACCT_TP_LINE_NO = `${this.addOffsetRows[index].LOCAL_ACCT_KEY}${this.addOffsetRows[index].TP_KEY}${line.LINE_NO}`;
      this.INVALID_OBJECT = 'N';
    }
    this.cdr.detectChanges();
  }

  validateDesc(): void {
    this.isMaxLimitReached = this.ADJ_DESCRIPTION.length >= 250;
  }

  removeMessage(): void {
    this.isMaxLimitReached = false;
  }

  updateFirTransType(transType: string): void {
    this.FIR_TRANS_TYPE = transType;
    this.cdr.detectChanges();
  }

  // DCS specific methods
  reclassCall(): void {
    this.activeScheduleTab = 1;
    this.reportableFlag = true;
    // Title stays "SCHDM Adjustments" for DCS - does not change on tab switch
    this.cdr.detectChanges();
  }

  reclassCall1(): void {
    this.activeScheduleTab = 0;
    this.reportableFlag = false;
    // Title stays "SCHDM Adjustments" for DCS
    this.cdr.detectChanges();
  }

  changePostingAcct(): void {
    this.testFlag = this.postingChange;
    this.cdr.detectChanges();
  }

  newPostingTaxChange(): void {
    // Handle posting tax account change for SCHDM (legacy newPostingTaxchange + checkPostingtaxerror).
    this.postingTaxAcctKey = this.resolvePostingAcctKey(this.postingTaxTop, this.glAcctsMData);
    this.postingTaxAccountCodeError = !this.postingTaxTop;
    this.cdr.detectChanges();
  }

  newPostingTaxChangeB(): void {
    // Handle posting tax account change for Book Reportable (legacy newPostingTaxchangeB).
    this.postingTaxAcctKeyB = this.resolvePostingAcctKey(this.postingTaxTopB, this.glAcctsMDataB);
    this.cdr.detectChanges();
  }

  changeTemplate(): void {
    // Mirror legacy `changeTemplate`:
    //   1. trigger SchdM type load (which cascades to TBBS),
    //   2. resolve user M-1 description + template key from `mainmodaldata`,
    //   3. run inline validations.
    // Legacy did NOT early-return for empty/separator templates; it still runs
    // validations so the required-field error surfaces.
    this.loadSchdMType();

    const templateData = this.mainModalData.find(item => item.SCHDM_TEMPLATE === this.template);
    if (templateData) {
      this.templateKey = templateData.SCHDM_TEMPLATE_ID;
      this.userM1 = templateData.USER_DESC || '';
    } else if (!this.template) {
      // Cleared selection -> drop derived state to match legacy fall-through.
      this.templateKey = '';
      this.userM1 = '';
    }

    this.checkTemplateError();
    this.cdr.detectChanges();
  }

  loadSchdMType(): void {
    // Resolve template key from currently selected template (legacy: schdm_temp).
    let schdmTemp: string = this.templateKey ? String(this.templateKey) : '';
    if (this.mainModalData?.length) {
      const match = this.mainModalData.find((item: any) => item.SCHDM_TEMPLATE === this.template);
      if (match) {
        schdmTemp = String(match.SCHDM_TEMPLATE_ID ?? '');
        this.templateKey = schdmTemp;
      }
    }

    // Mirror legacy `loadschdmtype` reset (note: legacy clears schdmt only in
    // the new-record path; the edit hydration path uses `loadschdmtype_temp`
    // which preserves the saved selection until the response sets a default).
    this.schdMLoading = true;
    this.schdMType = [];
    this.schdMTypeData = [];
    if (!this.isEdit) {
      this.schdMt = '';
    }
    this.cdr.detectChanges();

    if (!schdmTemp) {
      // No template selected -> bail out like legacy when there is no template
      // mapping but still clear loaders and re-run validation.
      this.schdMLoading = false;
      this.checkTypeError();
      this.loadTbbsData();
      this.cdr.detectChanges();
      return;
    }

    this.epAdjsService.loadSchdMTypes(
      this.GENERAL_CONFIG.base_url,
      this.rowData.LOCAL_CHART_KEY,
      this.rowData.LOCAL_ACCT_KEY,
      this.rowData.SYSTEM_ACCT_KEY,
      schdmTemp,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        this.schdMTypeData = data || [];
        const types = this.schdMTypeData.map((item: any) => item.SCHDM_TYPE).filter((t: any) => t);
        this.schdMType = [...new Set(types)];

        // Legacy: new-record path sets default from first row; edit path
        // (`type === true`) preserves the existing schdmt value.
        if (!this.isEdit) {
          const defaultType =
            this.schdMTypeData[0]?.SCHDM_TYPE_DEFAULT ?? this.schdMTypeData[0]?.SCHDM_TYPE ?? '';
          this.schdMt = defaultType;
        }

        this.schdMLoading = false;
        this.checkTypeError();
        // Always cascade to TBBS load like legacy.
        this.loadTbbsData();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading SchdM types:', error);
        this.schdMLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  typeChange(): void {
    // Mirror legacy `typeChange`:
    //   1. run type error check,
    //   2. clear TBBS selection,
    //   3. reload TBBS data (which will reset the default for new mode).
    this.checkTypeError();
    if (!this.isEdit) {
      this.tbbsAcctVal = '';
    }
    this.loadTbbsData();
    this.cdr.detectChanges();
  }

  loadTbbsData(): void {
    // Resolve schdm + template keys from currently selected values.
    let schdmTempTbbs: string = this.templateKey ? String(this.templateKey) : '';
    if (this.mainModalData?.length) {
      const tmplMatch = this.mainModalData.find((item: any) => item.SCHDM_TEMPLATE === this.template);
      if (tmplMatch) {
        schdmTempTbbs = String(tmplMatch.SCHDM_TEMPLATE_ID ?? '');
      }
    }

    let schdmTypeKey = '';
    if (this.schdMTypeData?.length) {
      const typeMatch = this.schdMTypeData.find((item: any) => item.SCHDM_TYPE === this.schdMt);
      if (typeMatch) {
        schdmTypeKey = String(typeMatch.SCHDM_TYPE_SAVE ?? '');
      }
    }

    this.tbbsLoading = true;
    this.tbbsAcct = [];
    this.tbbsAcctData = [];
    this.cdr.detectChanges();

    if (!schdmTempTbbs || !schdmTypeKey) {
      this.tbbsLoading = false;
      this.cdr.detectChanges();
      return;
    }

    this.epAdjsService.loadTbbsAccounts(
      this.GENERAL_CONFIG.base_url,
      this.rowData.LOCAL_CHART_KEY,
      this.rowData.LOCAL_ACCT_KEY,
      this.rowData.SYSTEM_ACCT_KEY,
      schdmTempTbbs,
      schdmTypeKey,
      this.rowData.COMBINATION_KEY,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        this.tbbsAcctData = data || [];
        const accts = this.tbbsAcctData.map((item: any) => item.TAX_STREAM_ACCT);
        this.tbbsAcct = [...new Set(accts)];

        // Legacy: always defaults to first TBBS row after load (both new and
        // edit-temp paths). For Angular edit mode, preserve the saved value if
        // it is still present in the list; otherwise fall back to default.
        if (this.tbbsAcct.length > 0) {
          if (this.isEdit) {
            const hasSavedAcct = this.tbbsAcct.includes(this.tbbsAcctVal);
            if (!hasSavedAcct) {
              this.tbbsAcctVal = this.tbbsAcctData[0].TAX_STREAM_ACCT;
            }
          } else {
            this.tbbsAcctVal = this.tbbsAcctData[0].TAX_STREAM_ACCT;
          }
        }

        this.tbbsLoading = false;
        this.tbbsChange();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading TBBS accounts:', error);
        this.tbbsLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  tbbsChange(): void {
    // Mirror legacy `tbbsChange` validation.
    this.checkTbbsError();
    this.cdr.detectChanges();
  }

  m1Desc(): void {
    // Mirror legacy `m1Desc` validation.
    this.checkUserDescError();
    this.cdr.detectChanges();
  }

  /** Legacy `checktemplateError`: also re-runs user description validation. */
  private checkTemplateError(): void {
    this.templateError = !this.template;
    this.checkUserDescError();
  }

  /** Legacy `checktypeError`. */
  private checkTypeError(): void {
    this.typeError = !this.schdMt;
  }

  /** Legacy `tbbsChange` validation body. */
  private checkTbbsError(): void {
    this.tbbsError = !this.tbbsAcctVal;
  }

  /** Legacy `m1Desc` validation body. */
  private checkUserDescError(): void {
    this.userDescError = !this.userM1;
  }

  /**
   * Mirror legacy `checkDCSData()` - run all DCS validations so the
   * aggregate error flags are accurate before gating the save.
   */
  private checkDcsData(): void {
    this.checkTemplateError();
    this.checkUserDescError();
    this.checkTypeError();
    this.checkTbbsError();
    this.cyAmount();
    this.checkSchdmReviewedError();
  }

  /** Legacy `reviewdChange` without arg + `checkdcsflag` true branch. */
  private checkSchdmReviewedError(): void {
    this.schdmReviewedError = !this.sch;
  }

  cyAmount(): void {
    // Legacy cyAmount: CY Schd M-1 Amount is required (0 is valid; empty/null is not).
    this.cyAmtError = this.CURRENT_YR_AMT === null
      || this.CURRENT_YR_AMT === undefined
      || (this.CURRENT_YR_AMT as any) === '';
    this.cdr.detectChanges();
  }

  reviewdChange(value: string): void {
    this.sch = value;
    if (value === 'Yes') {
      this.userReviewd = this.globalService?.userSettingsData?.user?.display_name || '';
      this.timestamp = new Date().toISOString();
      this.schdmReviewedError = false;
    }
    if (value === 'No') {
      this.userReviewd = '';
      this.timestamp = '';
      this.openSchdMModal();
      this.schdmReviewedError = false;
    }
    this.cdr.detectChanges();
  }

  openSchdMModal(): void {
    this.schdMComment = { subject: '', message: '', is_public: 0, notify: [] };
    this.schdMUserSearch = '';
    this.schdMUserResults = [];
    this.showSchdMModal = true;
    this.cdr.detectChanges();
  }

  closeSchdMModal(): void {
    this.showSchdMModal = false;
    this.cdr.detectChanges();
  }

  searchSchdMUsers(event: any): void {
    const val = (event.target as HTMLInputElement).value;
    this.schdMUserSearch = val;
    if (!val || val.length < 2) {
      this.schdMUserResults = [];
      this.cdr.detectChanges();
      return;
    }
    this.schdMUserLoading = true;
    const params = new URLSearchParams();
    params.set('action_id', '76');
    params.set('search_string', val);
    const hdrs = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    this.http.post<any>(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params.toString(),
      { headers: hdrs, withCredentials: true }
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (response: any) => {
        this.schdMUserResults = response?.jsonObject || [];
        this.schdMUserLoading = false;
        this.cdr.detectChanges();
      },
      () => { this.schdMUserResults = []; this.schdMUserLoading = false; this.cdr.detectChanges(); }
    );
  }

  addSchdMNotifyUser(user: any): void {
    if (!this.schdMComment.notify.find((u: any) => u.VALUE === user.VALUE)) {
      this.schdMComment.notify.push(user);
    }
    this.schdMUserSearch = '';
    this.schdMUserResults = [];
    this.cdr.detectChanges();
  }

  removeSchdMNotifyUser(index: number): void {
    this.schdMComment.notify.splice(index, 1);
    this.cdr.detectChanges();
  }

  saveSchdMComment(): void {
    if (!this.schdMComment.subject || !this.schdMComment.message) {
      this._snackBar.open('Subject and Message are required', 'Close', {
        duration: 4000, panelClass: ['warning-snackbar']
      });
      return;
    }
    this.schdMSaving = true;
    const notify = this.schdMComment.notify.map((u: any) => u.VALUE).join(',');
    const objContext: any = {
      state: '',
      filters: '{}',
      tableState: '{}',
      data: '{}',
      type: 'grid',
      screen_key: this.globalService?.globalParams?.screen_key || '',
      activity_key: this.globalService?.globalParams?.activity_key || '',
      row_key: '',
      col_key: '',
      element: '',
      comment_key: null,
      is_public: this.schdMComment.is_public,
      message: this.schdMComment.message,
      notify: notify,
      attachments: '',
      tags: '[]',
      follow_up: 0,
      follow_up_by: null,
      value: '',
      subject: this.schdMComment.subject,
      task_action_item: 0,
      task_target_date: '',
      assign: ''
    };
    const urlParams = new URLSearchParams();
    Object.keys(objContext).forEach(key => {
      if (objContext[key] !== undefined && objContext[key] !== null) {
        urlParams.set(key, objContext[key].toString());
      }
    });
    const hdrs = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    this.http.post<any>(
      this.GENERAL_CONFIG.base_url + '/addComment',
      urlParams.toString(),
      { headers: hdrs, withCredentials: true }
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (response: any) => {
        this.schdMSaving = false;
        if (response?.callSuccess === '1') {
          this._snackBar.open('Comment saved successfully', 'Close', {
            duration: 4000, panelClass: ['success-snackbar']
          });
        }
        this.closeSchdMModal();
      },
      () => {
        this.schdMSaving = false;
        this._snackBar.open('Error saving comment', 'Close', {
          duration: 4000, panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  loadProjectSteps(): void {
    if (!this.project) {
      this.projectSteps.list = [];
      this.projectStep = '';
      return;
    }

    this.projectStepLoading = true;
    const selectedProject = this.projectsList.list.find(p => p.PROJECT_NAME === this.project);

    if (selectedProject) {
      const steps = this.allProjectsWithSteps.filter(
        item => item.PROJECT_ID === selectedProject.PROJECT_ID
      );
      this.projectSteps.list = steps.sort((a, b) => a.STEP_NUMBER - b.STEP_NUMBER);
    } else {
      this.projectSteps.list = [];
    }

    this.projectStepLoading = false;
    this.cdr.detectChanges();
  }

  searchTradingPartners(event: any): void {
    const searchValue = event.target.value;
    if (searchValue.length < 3) {
      this.tpSearchResults = [];
      return;
    }

    this.loadingTpsDcs = true;
    this.epAdjsService.getTradingPartners(
      this.GENERAL_CONFIG.base_url,
      this.filterParams.tax_year || this.rowData.TAX_YEAR,
      this.filterParams.scenario || this.rowData.SCENARIO,
      searchValue,
      this.globalService
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        this.tpSearchResults = data;
        this.loadingTpsDcs = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.tpSearchResults = [];
        this.loadingTpsDcs = false;
        this.cdr.detectChanges();
      }
    );
  }

  setTradingPartnerDcs(tp: any): void {
    this.tpObjectD = tp;
    this.tpObjectDDisplay = tp.TP_ENTITY;
    this.tpSearchResults = [];
    this.cdr.detectChanges();
  }

  clickYes(): void {
    this.rptTFlag = 'Yes';
    this.cdr.detectChanges();
  }

  clickNo(): void {
    this.rptTFlag = 'No';
    this.cdr.detectChanges();
  }

  reportableActionChange(): void {
    const code = this.normalizeReportableActionCode(this.transactionType)
      || this.normalizeReportableActionCode(this.oldReportableAction);
    this.reportableActionChangeFlag = code;
    this.reportableActionError = !code;
    this.cdr.detectChanges();
  }

  reportableTaxAmountChange(): void {
    this.reportableTaxAmountError = !this.rptTaxAcct;
    this.cdr.detectChanges();
  }

  applyTaxAcct(): void {
    // Legacy applyTaxacct: copy the CY Schd M-1 Amount into Reportable Tax Amount, then validate.
    this.rptTaxAcct = this.CURRENT_YR_AMT;
    this.reportableTaxAmountChange();
    this.cdr.detectChanges();
  }

  // Build the Reportable Transaction settings payload for the SCHDM/DCS save (legacy 1018-1159).
  private buildReportableSettings(): any[] {
    const settings: any[] = [];
    const actionCode = this.resolveReportableActionCode(this.transactionType);
    const typeCode = this.resolveTypeReportableTransCode(this.typeOfReportableTrans);

    // In edit mode (legacy `vm.type === true`) OLD_* reflects the originally-saved values.
    const oldActionCode = this.isEdit ? this.oldReportableAction : actionCode;
    const oldTaxAmt = this.isEdit ? this.oldRptTaxAcct : this.rptTaxAcct;
    const oldTaxShelter = this.isEdit ? this.oldTaxShelterRegNum : this.taxShelterRegNum;
    const oldTransDesc = this.isEdit ? this.oldReportableTransDesc : this.reportableTransDesc;
    const oldTypeCode = this.isEdit ? this.oldTypeOfReportableTrans : typeCode;
    const oldReason = this.isEdit ? this.oldReasonForChange : this.reasonForChange;

    // One entry per reportable G/L row (legacy tempobj).
    for (const row of this.reportableList) {
      const glAcctKey = row.glacct ? row.glacct.GL_ACCT_KEY : undefined;
      // Legacy: existing ('E') rows use their saved old key/amount; new ('I') rows use current.
      const oldGlAcctKey = (this.isEdit && row.type === 'E') ? row.old_gl_acct_key : glAcctKey;
      const oldThisAdjAmt = (this.isEdit && row.type === 'E') ? row.old_thisadj_amt : row.thisadj_amt;
      settings.push({
        NEW_REPORTABLE_ACTION: actionCode,
        OLD_REPORTABLE_ACTION: oldActionCode,
        OLD_REPORTABLE_TAX_AMOUNT: oldTaxAmt,
        OLD_TAX_SHELTER_REGISTRATION_NUMBER: oldTaxShelter,
        OLD_REPORTABLE_TRANSACTION_DESCRIPTION: oldTransDesc,
        OLD_TYPE_REPORTABLE_TRANSACTION: oldTypeCode,
        OLD_REASON_FOR_ZERO_BOOK_AMT: oldReason,
        OLD_GL_ACCT: oldGlAcctKey,
        OLD_THIS_ADJ_AMT: oldThisAdjAmt,
        NEW_REPORTABLE_TAX_AMOUNT: this.rptTaxAcct,
        NEW_TAX_SHELTER_REGISTRATION_NUMBER: this.taxShelterRegNum,
        NEW_REASON_FOR_ZERO_BOOK_AMT: this.reasonForChange,
        NEW_REPORTABLE_TRANSACTION_DESCRIPTION: this.reportableTransDesc,
        NEW_TYPE_REPORTABLE_TRANSACTION: typeCode,
        NEW_GL_ACCT: glAcctKey,
        NEW_THIS_ADJ_AMT: row.thisadj_amt
      });
    }

    // Static summary entry (legacy tempobjStatic) using the reportable GL account + total adj amount.
    const rtAcctKey = this.glacctsRpt && this.glacctsRpt.length > 0 ? this.glacctsRpt[0].RT_ACCT_KEY : undefined;
    settings.push({
      OLD_REPORTABLE_ACTION: oldActionCode,
      OLD_REPORTABLE_TAX_AMOUNT: oldTaxAmt,
      OLD_TAX_SHELTER_REGISTRATION_NUMBER: oldTaxShelter,
      OLD_REPORTABLE_TRANSACTION_DESCRIPTION: oldTransDesc,
      OLD_TYPE_REPORTABLE_TRANSACTION: oldTypeCode,
      OLD_REASON_FOR_ZERO_BOOK_AMT: oldReason,
      NEW_REPORTABLE_ACTION: actionCode,
      NEW_REPORTABLE_TAX_AMOUNT: this.rptTaxAcct,
      NEW_TAX_SHELTER_REGISTRATION_NUMBER: this.taxShelterRegNum,
      NEW_REPORTABLE_TRANSACTION_DESCRIPTION: this.reportableTransDesc,
      NEW_TYPE_REPORTABLE_TRANSACTION: typeCode,
      NEW_REASON_FOR_ZERO_BOOK_AMT: this.reasonForChange,
      OLD_GL_ACCT: rtAcctKey,
      NEW_GL_ACCT: rtAcctKey,
      OLD_THIS_ADJ_AMT: this.thisAdjAmt1,
      NEW_THIS_ADJ_AMT: this.thisAdjAmt1
    });

    return settings;
  }

  rptDescChange(): void {
    this.rptDescError = !this.reportableTransDesc;
    this.cdr.detectChanges();
  }

  rptDescChangeBook(): void {
    this.rptDescErrorBook = !this.reportableTransDescBook;
    this.cdr.detectChanges();
  }

  taxShelterChangeBook(): void {
    this.taxShelterErrorBook = !this.taxShelterRegNumBook;
    this.cdr.detectChanges();
  }

  typeOfRptTransChange(): void {
    this.typeOfRptError = !this.typeOfReportableTrans;
    this.cdr.detectChanges();
  }

  typeOfRptTransChangeBook(): void {
    this.typeOfRptErrorBook = !this.typeOfReportableTransBook;
    this.cdr.detectChanges();
  }

  reasonForZeroBookAmtChange(): void {
    this.reasonForZeroError = !this.reasonForChange;
    this.cdr.detectChanges();
  }

  addReportable(): void {
    const newItem: ReportableItemModel = {
      glacct: null,
      glacct_book: null,
      cy_amt: null,
      cy_amt_book: null,
      earlieradj_amt: null,
      earlieradj_amt_book: null,
      thisadj_amt: null,
      thisadj_amt_book: null,
      adj_cy_amt: null,
      adj_cy_amt_book: null,
      glacct_error: false,
      glacct_error_book: false,
      thisadj_amt_error: false,
      thisadj_amt_error_book: false,
      cy_amt_loading: false,
      cy_amt_book_loading: false,
      earlieradj_amt_loading: false,
      earlieradj_amt_book_loading: false,
      type: 'I'
    };
    this.reportableList.push(newItem);
    this.cdr.detectChanges();
  }

  removeReportable(index: number): void {
    this.reportableList.splice(index, 1);
    this.cdr.detectChanges();
  }

  glacctChange(item: ReportableItemModel): void {
    item.glacct_error = !item.glacct;
    if (item.glacct) {
      this.getReportableGlAcctChangeData(item);
    }
    this.cdr.detectChanges();
  }

  // Legacy getreportableglAcctchangedata: load CY / Earlier Adjustment amounts for the selected reportable G/L account.
  private getReportableGlAcctChangeData(item: ReportableItemModel): void {
    const glAcctKey = item.glacct ? item.glacct.GL_ACCT_KEY : undefined;
    if (glAcctKey === undefined || glAcctKey === null) {
      return;
    }
    item.cy_amt_loading = true;
    item.earlieradj_amt_loading = true;
    this.cdr.detectChanges();

    const params = {
      cc_key: this.rowData ? this.rowData.COMBINATION_KEY : undefined,
      gl_acct_key: glAcctKey
    };

    this.epAdjsService.getReportableGlAcctChangeData(this.GENERAL_CONFIG.base_url, params, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any[]) => {
          item.cy_amt = 0;
          item.earlieradj_amt = 0;
          if (data && data.length > 0) {
            if (data[0].CY_AMOUNT !== undefined) {
              item.cy_amt = data[0].CY_AMOUNT;
            }
            if (data[0].EARLY_ADJ_AMT !== undefined) {
              item.earlieradj_amt = data[0].EARLY_ADJ_AMT;
            }
          }
          item.adj_cy_amt = (item.cy_amt || 0) + (item.earlieradj_amt || 0) + (item.thisadj_amt || 0);
          item.cy_amt_loading = false;
          item.earlieradj_amt_loading = false;
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('Error loading reportable G/L account change data:', error);
          item.cy_amt_loading = false;
          item.earlieradj_amt_loading = false;
          this.cdr.detectChanges();
        }
      );
  }

  adjustedAmtChange(index?: number): void {
    // Per-row adjusted CY amount (legacy adjusted_amt_change).
    if (index !== undefined) {
      const item = this.reportableList[index];
      item.thisadj_amt = parseInt(item.thisadj_amt as any, 10) || 0;
      item.adj_cy_amt = (parseInt(item.cy_amt as any, 10) || 0) + (parseInt(item.earlieradj_amt as any, 10) || 0) + (item.thisadj_amt || 0);
    }

    // Recompute totals across all reportable rows (legacy total_this_adj_amt / thisadj_amt1).
    this.totalThisAdjAmt = 0;
    for (const row of this.reportableList) {
      if (row.thisadj_amt === '' as any || row.thisadj_amt === undefined || row.thisadj_amt === null) {
        row.thisadj_amt = 0;
      }
      this.totalThisAdjAmt += parseInt(row.thisadj_amt as any, 10) || 0;
      row.thisadj_amt_error = row.thisadj_amt === 0;
    }
    this.thisAdjAmt1 = this.totalThisAdjAmt * -1;
    this.cdr.detectChanges();
  }

  addReportableBook(): void {
    const newItem: ReportableItemModel = {
      glacct: null,
      glacct_book: null,
      cy_amt: null,
      cy_amt_book: 0,
      earlieradj_amt: null,
      earlieradj_amt_book: 0,
      thisadj_amt: null,
      thisadj_amt_book: 0,
      adj_cy_amt: null,
      adj_cy_amt_book: 0,
      glacct_error: false,
      glacct_error_book: false,
      thisadj_amt_error: false,
      thisadj_amt_error_book: false,
      cy_amt_loading: false,
      cy_amt_book_loading: false,
      earlieradj_amt_loading: false,
      earlieradj_amt_book_loading: false,
      type: 'I'
    };
    this.reportableListBook.push(newItem);
    this.cdr.detectChanges();
  }

  removeReportableBook(index: number): void {
    this.reportableListBook.splice(index, 1);
    this.adjustedAmtChangeBook();
    this.cdr.detectChanges();
  }

  glacctChangeBook(item: ReportableItemModel): void {
    item.glacct_error_book = !item.glacct_book;
    this.glacctErrorBook = this.reportableListBook.some(r => !r.glacct_book);
    if (item.glacct_book) {
      this.getReportableGlAcctChangeDataBook(item);
    }
    this.cdr.detectChanges();
  }

  // Legacy getreportableglAcctchangedata_book: load CY / Earlier amounts for a book reportable G/L account.
  private getReportableGlAcctChangeDataBook(item: ReportableItemModel): void {
    const glAcctKey = item.glacct_book ? item.glacct_book.GL_ACCT_KEY : undefined;
    if (glAcctKey === undefined || glAcctKey === null) {
      return;
    }
    item.cy_amt_book_loading = true;
    item.earlieradj_amt_book_loading = true;
    this.cdr.detectChanges();

    const params = {
      cc_key: this.rowData ? this.rowData.COMBINATION_KEY : undefined,
      gl_acct_key: glAcctKey
    };

    this.epAdjsService.getReportableGlAcctChangeData(this.GENERAL_CONFIG.base_url, params, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any[]) => {
          item.cy_amt_book = 0;
          item.earlieradj_amt_book = 0;
          if (data && data.length > 0) {
            if (data[0].CY_AMOUNT !== undefined) {
              item.cy_amt_book = data[0].CY_AMOUNT;
            }
            if (data[0].EARLY_ADJ_AMT !== undefined) {
              item.earlieradj_amt_book = data[0].EARLY_ADJ_AMT;
            }
          }
          item.adj_cy_amt_book = (item.cy_amt_book || 0) + (item.earlieradj_amt_book || 0) + (item.thisadj_amt_book || 0);
          item.cy_amt_book_loading = false;
          item.earlieradj_amt_book_loading = false;
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('Error loading book reportable G/L account change data:', error);
          item.cy_amt_book_loading = false;
          item.earlieradj_amt_book_loading = false;
          this.cdr.detectChanges();
        }
      );
  }

  // Legacy adjusted_amt_change_book: per-row adjusted amount + book static totals.
  adjustedAmtChangeBook(index?: number): void {
    if (index !== undefined) {
      const row = this.reportableListBook[index];
      row.thisadj_amt_book = parseInt(row.thisadj_amt_book as any, 10) || 0;
      row.adj_cy_amt_book = (parseInt(row.cy_amt_book as any, 10) || 0) + (parseInt(row.earlieradj_amt_book as any, 10) || 0) + (row.thisadj_amt_book || 0);
    }

    this.thisAdjAmtErrorBook = false;
    this.totalThisAdjAmtBook = 0;
    for (const row of this.reportableListBook) {
      if (row.thisadj_amt_book === '' as any || row.thisadj_amt_book === undefined || row.thisadj_amt_book === null) {
        row.thisadj_amt_book = 0;
      }
      this.totalThisAdjAmtBook += parseInt(row.thisadj_amt_book as any, 10) || 0;
    }
    this.thisAdjAmt1Book = this.totalThisAdjAmtBook * -1;
    this.totalThisAdjAmtStaticBook = this.thisAdjAmt1Book + this.totalThisAdjAmtBook;
    this.adjCyAmt1Book = this.thisAdjAmt1Book;
    for (const row of this.reportableListBook) {
      if (row.thisadj_amt_book === 0) {
        row.thisadj_amt_error_book = true;
        this.thisAdjAmtErrorBook = true;
      } else {
        row.thisadj_amt_error_book = false;
      }
    }
    this.cdr.detectChanges();
  }

  calculateBookTotals(): void {
    let total = 0;
    this.reportableListBook.forEach(item => {
      total += item.thisadj_amt_book || 0;
    });
    this.totalThisAdjAmtStaticBook = total;
  }

  private loadExistingChartData(): void {
    this.existingChartLoading = true;
    this.noRecordsBook = false;

    const params = {
      tax_year: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.filterParams.jcd_key,
      cc_key: this.rowData.COMBINATION_KEY,
      local_acct_key: this.rowData.LOCAL_ACCT_KEY
    };

    this.epAdjsService.loadExistingBookReportable(this.GENERAL_CONFIG.base_url, params, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any[]) => {
          console.log('Existing chart data raw:', data);
          // Keep the full (ungrouped) rows for edit hydration (legacy existing_chart_data_temp).
          this.existingChartDataTemp = data || [];
          // Group by REF_KEY (take first item per unique REF_KEY)
          const lookup: { [key: string]: boolean } = {};
          this.existingChartData = [];

          data.forEach(item => {
            const refKey = item.REF_KEY;
            if (refKey && !lookup[refKey]) {
              lookup[refKey] = true;
              this.existingChartData.push(item);
            }
          });

          console.log('Existing chart data formatted:', this.existingChartData);
          this.existingChartLoading = false;
          this.noRecordsBook = this.existingChartData.length === 0;
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('Error loading existing chart data:', error);
          this.existingChartLoading = false;
          this.noRecordsBook = true;
          this.cdr.detectChanges();
        }
      );
  }

  // Legacy loadAdjustment_b: hydrate the Book Reportable form from an existing record for editing.
  loadAdjustmentBook(index: number): void {
    this.reportableListBook = [];
    this.typeBook = true;
    const data = this.existingChartData[index];
    this.refKey = data.REF_KEY;

    this.oldReportableTransDescBook = data.REPORTABLE_TRANSACTION_DESCRIPTION;
    this.reportableTransDescBook = data.REPORTABLE_TRANSACTION_DESCRIPTION;
    this.oldTaxShelterRegNumBook = data.TAX_SHELTER_REGISTRATION_NUMBER;
    this.taxShelterRegNumBook = data.TAX_SHELTER_REGISTRATION_NUMBER;
    this.oldTypeOfReportableTransBook = data.TYPE_REPORTABLE_TRANSACTION;
    const typeValue = data.TYPE_REPORTABLE_TRANSACTION;
    const typeCode = this.resolveTypeReportableTransCode(typeValue);
    this.typeOfReportableTransBook = typeCode ? this.findReportableTypeOption(typeCode) : null;
    this.pendingBookReportableType = typeValue && !typeCode && !this.reportableTransList.length
      ? typeValue
      : null;

    // Rebuild the rows from the ungrouped data for this REF_KEY (legacy existing_chart_data_temp loop).
    for (const row of this.existingChartDataTemp) {
      if (row.REF_KEY === this.refKey) {
        if (row.GL_ACCT !== undefined && row.GL_ACCT !== '5555555 - Reportable Transactions') {
          this.reportableListBook.push({
            glacct: null,
            glacct_book: row.GL_ACCT,
            old_gl_acct_key: row.GL_ACCT_KEY,
            old_thisadj_amt: row.ADJ_AMOUNT,
            cy_amt: null,
            cy_amt_book: row.CY_AMOUNT,
            earlieradj_amt: null,
            earlieradj_amt_book: row.EARLY_ADJ_AMT,
            thisadj_amt: null,
            thisadj_amt_book: row.ADJ_AMOUNT,
            adj_cy_amt: null,
            adj_cy_amt_book: 0,
            glacct_error: false,
            glacct_error_book: false,
            thisadj_amt_error: false,
            thisadj_amt_error_book: false,
            cy_amt_loading: false,
            cy_amt_book_loading: false,
            earlieradj_amt_loading: false,
            earlieradj_amt_book_loading: false,
            type: 'E'
          });
        } else if (row.GL_ACCT === '5555555 - Reportable Transactions') {
          this.glacct1Book = row.GL_ACCT;
          this.thisAdjAmt1Book = row.ADJ_AMOUNT;
        }
      }
    }

    this.adjustedAmtChangeBook();
    for (const row of this.reportableListBook) {
      row.adj_cy_amt_book = (row.cy_amt_book || 0) + (row.earlieradj_amt_book || 0) + (row.thisadj_amt_book || 0);
    }
    this.cdr.detectChanges();
  }

  private loadExistingAdjustments(): void {
    this.existingLoading = true;

    const isDCS = this.rowData.SOURCE_SYSTEM === 'DCS';

    const loadMethod = isDCS
      ? this.epAdjsService.loadExistingAdjustmentsDCS(
          this.GENERAL_CONFIG.base_url,
          {
            screen_key: this.mainScreenKey,
            combination_key: this.rowData.COMBINATION_KEY,
            chart_key: this.rowData.LOCAL_CHART_KEY,
            local_acct_key: this.rowData.SYSTEM_ACCT_KEY || 0
          },
          this.globalService
        )
      : this.epAdjsService.loadExistingAdjustmentsFIR(
          this.GENERAL_CONFIG.base_url,
          {
            screen_key: this.mainScreenKey,
            combination_key: this.rowData.COMBINATION_KEY,
            chart_key: this.rowData.LOCAL_CHART_KEY
          },
          this.globalService
        );

    loadMethod.pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any[]) => {
        console.log('Existing adjustments raw data:', data);
        // Keep the raw (ungrouped) rows for SchdM reportable edit hydration (legacy existingAdjustments_temp).
        this.existingAdjustmentsTemp = data || [];
        this.existingAdjustments = this.formatExistingData(data);
        console.log('Existing adjustments formatted:', this.existingAdjustments);
        this.existingLoading = false;
        this.noRecords = this.existingAdjustments.length === 0;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading existing adjustments:', error);
        this.existingLoading = false;
        this.noRecords = true;
        this.cdr.detectChanges();
      }
    );
  }

  private formatExistingData(data: any[]): any[] {
    // For DCS, group by SCHDM_ID
    if (this.SOURCE_SYSTEM === 'DCS') {
      const lookup: { [key: string]: boolean } = {};
      const adjustments: any[] = [];

      data.forEach(item => {
        const schdmId = item.SCHDM_ID;
        if (schdmId && !lookup[schdmId]) {
          lookup[schdmId] = true;
          adjustments.push({ ...this.buildMergedSchdmRow(data, schdmId, item), open: false });
        }
      });

      return adjustments;
    }

    // For FIR, group by TRANS_HEADER_KEY and ROW_INDICATOR
    const adjustments: any[] = [];
    const accounts: any[] = [];

    data.forEach(item => {
      if (item.ROW_INDICATOR === 1) {
        const adjObj = { ...item, ACCOUNTS: [], open: false };
        adjustments.push(adjObj);
      } else {
        const accountObj = { ...item, TYPE: 'edit' };
        accounts.push(accountObj);
      }
    });

    adjustments.forEach(adj => {
      adj.ACCOUNTS = accounts.filter(
        account => account.TRANS_HEADER_KEY === adj.TRANS_HEADER_KEY
      );
    });

    return adjustments;
  }

  setAccount(account: any, index: number): void {
    if (!account) return;

    const currAcct = this.accounts[index];
    currAcct.LOCAL_ACCT_KEY = account.LOCAL_ACCT_KEY;
    currAcct.SYSTEM_ACCT_KEY = account.SYSTEM_ACCT_KEY;
    currAcct.SYSTEM_ACCT = account.SYSTEM_ACCT;
    currAcct.SYSTEM_ACCT_DESC = account.SYSTEM_ACCT_DESC;
    currAcct.LOCAL_ACCT = account.LOCAL_ACCT;
    currAcct.LOCAL_ACCT_DESC = account.LOCAL_ACCT_DESC;
    currAcct.TYPE = 'target';
    currAcct.ACCTTP = `${account.LOCAL_ACCT_KEY}${currAcct.TP_KEY}${currAcct.TP_REP_PD}`;

    this.cdr.detectChanges();
  }

  setTradingPartner(tp: any, index: number): void {
    if (!tp) return;

    const currAcct = this.accounts[index];
    currAcct.TP_ENTITY = `${tp.TP_LEID}-${tp.TP_CDR_NO}(Pd:${tp.TP_REP_PD})`;
    currAcct.TP_KEY = tp.TP_KEY;
    currAcct.TP_REP_PD = tp.TP_REP_PD;
    currAcct.ACCTTP = `${currAcct.LOCAL_ACCT_KEY}${tp.TP_KEY}${tp.TP_REP_PD}`;

    this.cdr.detectChanges();
  }

  setLine(line: any): void {
    this.selectedLine = line;
    this.cdr.detectChanges();
  }

  setAdjType(adjType: any): void {
    this.selectedAdjType = adjType;
    this.cdr.detectChanges();
  }

  setAccountTotal(index: number): void {
    const account = this.accounts[index];
    const bookAmt = parseFloat(String(account.BOOK_AMT)) || 0;
    const taxAmt = parseFloat(String(account.TAX_AMT)) || 0;
    const curAdj = parseFloat(String(account.CUR_ADJ_AMT)) || 0;

    account.TOTAL = bookAmt + taxAmt + curAdj;
    this.setAdjTotal();
  }

  private setAdjTotal(): void {
    let total = 0;
    this.accounts.forEach(account => {
      if (account.CUR_ADJ_AMT !== null && account.CUR_ADJ_AMT !== undefined && !isNaN(Number(account.CUR_ADJ_AMT))) {
        total += parseFloat(String(account.CUR_ADJ_AMT));
      }
    });
    this.adjTotal = total;
    this.cdr.detectChanges();
  }

  showDetails(index: number, show: boolean): void {
    this.existingAdjustments[index].open = show;
    this.cdr.detectChanges();
  }

  loadAdjustment(index: number): void {
    this.delTransDetails = [];
    const data = this.existingAdjustments[index];

    if (this.SOURCE_SYSTEM === 'DCS') {
      this.isEdit = true;
      // Load DCS specific fields
      this.dcsSchdmId = data.SCHDM_ID || 0;
      this.tpObjectD = data.TP_KEY ? { TP_ENTITY: data.TP_ENTITY, TP_KEY: data.TP_KEY } : null;
      this.tpObjectDDisplay = data.TP_ENTITY || '';

      // Set display flags
      this.partnershipFlag = false;
      this.custom2AmtFlag = data.ADJ_SOURCE !== 'Custom';
      this.hideMandatoryFieldCSchdmType = data.ADJ_SOURCE !== 'Custom';
      this.hideMandatoryFieldCP = data.ADJ_SOURCE !== 'Custom' && data.ADJ_SOURCE !== 'Predefined';

      if (['FACS', 'INTPCS', 'EXTPCS', 'GHOST', 'PYPCS'].includes(data.ADJ_SOURCE_TYPE)) {
        this.hideMandatoryFieldCP = false;
        this.partnershipFlag = true;
      }

      this.hideSaveProvY = data.PROVISION_M1 !== 'Y';
      this.hideCgni = data.FLC_CODE === 'CGNI';

      // Load form fields
      this.custom2Amt = data.COL_2_AMT;
      this.refKey = data.REFERENCE_KEY;
      this.userM1 = data.USER_M1_DESC || '';
      this.templateKey = data.TEMPLATE_ID != null ? String(data.TEMPLATE_ID) : '';
      this.template = data.TEMPLATE || '';
      this.schdMt = data.SCHDM_TYPE || '';
      this.oldSchdMId = (data.SCHDM_TYPE_ID != null) ? data.SCHDM_TYPE_ID : (data.SCHDM_TYPE || '');
      this.tbbsAcctVal = data.TBBS_ACCT || '';
      this.CURRENT_YR_AMT = data.CY_M1_AMT;
      this.recurring = data.RECUR_FLAG === 'Y';
      this.bookAmtExist = data.NO_BOOK_AMT === 'Y';
      this.sch = data.SCHDM_REVIEW === 'Y' ? 'Yes' : 'No';
      this.userReviewd = data.USER_REVIEW;
      this.timestamp = data.REVIEW_ON;
      this.project = data.PROJECT_NAME;
      this.projectStep = data.PROJECT_STEP_NAME;
      this.settledYesNo = data.SETTLE_YN === 'Y' ? 'Yes' : 'No';
      this.provision = data.PROVISION_M1 === 'Y' ? 'Yes' : 'No';
      this.COMMENTS = data.COMMENTS || '';
      this.oldDcsComments = data.COMMENTS || '';
      this.pendingDcsEditRow = data;
      this.hydrateDcsReportableFields(data);
      this.rebuildReportableListFromExisting();

      // Mirror legacy edit hydration (`loadschdmtype_temp` + `loadtbbsdata_temp`):
      // reload the SchdM type and TBBS dropdowns for the saved template while
      // preserving the currently-selected `schdMt` / `tbbsAcctVal` values. The
      // cascade also re-runs validation so the mandatory-field error state is
      // accurate the moment the modal opens in edit mode.
      this.loadSchdMType();
      this.checkTemplateError();

      this.loadProjectSteps();

      // Re-bind selects after the reportable panel (*ngIf) and option lists render.
      setTimeout(() => {
        this.reapplyStoredReportableSelects();
        this.rebuildReportableListFromExisting();
      }, 0);
    } else {
      // FIR — hydrate form bindings (parentObject, dropdowns), matching AngularJS loadAdjustment
      this.isEdit = true;
      this.addOffsetRows = [];
      this.ADJ_DESCRIPTION = data.ADJ_DESC ?? '';
      (this as any).ADJ_SOURCE_TYPE = data.ADJ_SOURCE_TYPE;
      this.TRANS_HEADER_KEY = data.TRANS_HEADER_KEY;
      this.FIR_TRANS_TYPE = data.TRANS_TYPE;
      this.viewOnly = data.SHORT_ADJ_TYPE === 'A';

      this.existingOffsetAdjustments =
        data.ACCOUNTS && data.ACCOUNTS.length > 1
          ? data.ACCOUNTS.filter((acc: any) => acc.ROW_INDICATOR > 2)
          : [];

      if (data.ACCOUNTS?.[0]) {
        this.pendingFirEditSync = { account: data.ACCOUNTS[0], header: data };
        this.syncFirEpAdjFormForEdit(data.ACCOUNTS[0], data);
      } else {
        this.pendingFirEditSync = null;
      }

      this.accounts = (data.ACCOUNTS || []).map((acc: any) => ({
        ...acc,
        TYPE: 'edit'
      }));
      this.accounts.forEach((account: any, key: number) => {
        this.setAccountTotal(key);
      });
      this.setAdjTotal();
    }

    this.cdr.detectChanges();
  }

  deleteAdjustment(adjustment: any, type: string = 'PARENT'): void {
    this.existingLoading = true;

    const deleteData = {
      trans_header_key: adjustment.TRANS_HEADER_KEY,
      type: 'D',
      combination_key: this.rowData.COMBINATION_KEY,
      chart_key: this.rowData.LOCAL_CHART_KEY
    };

    this.epAdjsService.deleteAdjustment(
      this.GENERAL_CONFIG.base_url,
      deleteData,
      this.globalService,
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (response: any) => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.existingLoading = false;
        } else {
          this._snackBar.open('Adjustment has been successfully deleted', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this._snackBar.open('Error deleting adjustment', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.existingLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  reset(): void {
    this.isInitialized = false;
    this.isLoading = false;
    this.accounts = [];
    this.delTransDetails = [];
    this.existingOffsetAdjustments = [];
    this.addOffsetRows = [];
    this.isEdit = false;
    this.pendingFirEditSync = null;
    this.pendingDcsEditRow = null;
    this.pendingReportableActionCode = '';
    this.pendingReportableTypeCode = '';
    this.pendingBookReportableType = null;
    this.formSubmitted = false;
    this.firTpSearchText = '';
    this.firTpSearchResults = [];
    this.offsetAcctSearchResults = [];
    this.offsetTpSearchResults = [];
    this.ADJ_DESCRIPTION = '';
    this.TRANS_HEADER_KEY = '';
    this.selectedLine = null;
    this.selectedAdjType = null;
    this.viewOnly = false;

    // Reset DCS fields
    this.userM1 = '';
    this.template = '';
    this.schdMt = '';
    this.tbbsAcctVal = '';
    this.CURRENT_YR_AMT = null;
    this.recurring = false;
    this.bookAmtExist = false;
    this.sch = '';
    this.project = '';
    this.projectStep = '';
    this.settledYesNo = 'No';
    this.COMMENTS = '';
    this.oldDcsComments = '';
    this.dcsSchdmId = 0;
    this.reportable = 'No';
    this.rptTFlag = 'No';
    this.custom2Amt = null;
    this.codeCombination = null;

    // Reset reportable (SchdM) edit state so a new record starts clean (legacy init).
    this.transactionType = '';
    this.rptTaxAcct = null;
    this.reasonForChange = '';
    this.reportableTransDesc = '';
    this.taxShelterRegNum = '';
    this.typeOfReportableTrans = '';
    this.reportableActionChangeFlag = '';
    this.reportableList = [];
    this.thisAdjAmt1 = null;
    this.totalThisAdjAmt = 0;
    this.oldReportableAction = '';
    this.oldRptTaxAcct = null;
    this.oldReasonForChange = '';
    this.oldReportableTransDesc = '';
    this.oldTaxShelterRegNum = '';
    this.oldTypeOfReportableTrans = '';
    this.reportableActionError = false;
    this.reportableTaxAmountError = false;
    this.rptDescError = false;
    this.typeOfRptError = false;
    this.reasonForZeroError = false;

    this.init();
  }

  save(): void {
    this.formSubmitted = true;
    this.cdr.detectChanges();
    // Validate based on source system
    if (this.SOURCE_SYSTEM === 'FIR') {
      if (!this.ADJ_DESCRIPTION || this.ADJ_DESCRIPTION.trim() === '') {
        this._snackBar.open('Adjustment Description is required', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
        return;
      }
    } else {
      // DCS validation - mirror legacy `checkDCSData` aggregate check.
      this.checkDcsData();

      // New Posting Acct variant requires a posting tax account selection (legacy checkPostingtaxerror).
      if (this.testFlag) {
        this.postingTaxAccountCodeError = !this.postingTaxTop;
      }

      const hasMandatoryError =
        this.templateError ||
        this.userDescError ||
        this.cyAmtError ||
        this.typeError ||
        this.tbbsError ||
        this.schdmReviewedError ||
        (this.testFlag && this.postingTaxAccountCodeError);

      if (hasMandatoryError) {
        this._snackBar.open('Please correct the errors below', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
        this.cdr.detectChanges();
        return;
      }

      // Reportable Transaction validation (legacy save 789-838).
      if (this.rptTFlag === 'Yes') {
        const actionCode = this.resolveReportableActionCode(this.transactionType);
        let reportableError = this.reportableActionError || this.reportableTaxAmountError;
        if (actionCode === 'DP') {
          reportableError = reportableError || this.rptDescError || this.typeOfRptError;
        } else if (actionCode === 'BAA') {
          const rowError = this.reportableList.some(r => r.glacct_error || r.thisadj_amt_error);
          reportableError = reportableError || this.rptDescError || this.typeOfRptError || rowError;
        } else if (actionCode === 'ZBA') {
          reportableError = reportableError || this.reasonForZeroError || this.rptDescError || this.typeOfRptError;
        }
        if (reportableError) {
          this._snackBar.open('Please correct the errors below', 'Close', {
            duration: 4000,
            panelClass: ['warning-snackbar']
          });
          this.cdr.detectChanges();
          return;
        }
      }
    }

    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    if (this.SOURCE_SYSTEM === 'DCS') {
      this.saveDCS();
    } else {
      this.saveFIR();
    }
  }

  private saveFIR(): void {
    const isEdit = this.TRANS_HEADER_KEY !== '' && this.TRANS_HEADER_KEY !== 0;
    const txIssueId = this.globalService?.globalParams?.issue_id_for_tx_save;
    const issueKey = (txIssueId === undefined || txIssueId === '')
      ? (this.globalService?.globalParams?.issue_id ? parseInt(this.globalService.globalParams.issue_id) : null)
      : txIssueId;

    const headerObj: any = {
      tax_year: this.filterParams.tax_year || this.globalService?.globalParams?.tax_year,
      ADJ_DESC: this.ADJ_DESCRIPTION,
      ADJ_ORIGIN_SOURCE: this.SOURCE_SYSTEM,
      ADJ_SOURCE_TYPE: this.SOURCE_SYSTEM === 'DCS' ? '' : ((this as any).ADJ_SOURCE_TYPE || 'MA'),
      SUPPL_INFO: this.SUPPL_INFO,
      TYPE: isEdit ? 'U' : 'S',
      TRANS_HEADER_KEY: isEdit ? this.TRANS_HEADER_KEY : 0,
      SOURCE_SYSTEM_FLAG: this.SOURCE_SYSTEM === 'DCS' ? 'D' : 'F',
      CHART_KEY: this.rowData.LOCAL_CHART_KEY,
      COMBINATION_KEY: this.rowData.COMBINATION_KEY,
      FIR_TRANS_TYPE: this.FIR_TRANS_TYPE,
      ISSUE_KEY: issueKey
    };

    const transactions: any[] = [];

    transactions.push({
      ACCT_KEY: this.parentObject.LOCAL_ACCT_KEY,
      CHART_KEY: this.parentObject.CHART_KEY,
      ADJ_AMT: this.parentObject.ADJ_AMT,
      MULTIPLIER: this.parentObject.MULTIPLIER,
      PROJECT_ID: this.parentObject.PROJECT_ID,
      PROJECT_STEP_ID: this.parentObject.PROJECT_STEP_ID,
      AUTO_REVERSE_YN: this.parentObject.AUTO_REVERSE_YN,
      SCHD_H_LINE_NO: this.parentObject.SCHD_H_LINE_NO,
      TP_KEY: this.parentObject.TP_KEY,
      TRANS_DETAILS_KEY: this.parentObject.TRANS_DETAILS_KEY,
      POSTING_GL_ACCT_KEY: this.parentObject.POSTING_GL_ACCT_KEY || ''
    });

    this.addOffsetRows.forEach(offset => {
      transactions.push({
        ACCT_KEY: offset.LOCAL_ACCT_KEY,
        CHART_KEY: offset.CHART_KEY,
        ADJ_AMT: offset.ADJ_AMT,
        MULTIPLIER: offset.MULTIPLIER,
        PROJECT_ID: offset.PROJECT_ID,
        PROJECT_STEP_ID: offset.PROJECT_STEP_ID,
        AUTO_REVERSE_YN: offset.AUTO_REVERSE_YN,
        SCHD_H_LINE_NO: offset.SCHD_H_LINE_NO,
        TP_KEY: offset.TP_KEY,
        TRANS_DETAILS_KEY: offset.TRANS_DETAILS_KEY,
        POSTING_GL_ACCT_KEY: ''
      });
    });

    if (isEdit && this.existingOffsetAdjustments.length) {
      this.existingOffsetAdjustments.forEach((val: any) => {
        transactions.push({
          ACCT_KEY: val.LOCAL_ACCT_KEY,
          CHART_KEY: val.LOCAL_CHART_KEY,
          ADJ_AMT: val.ADJ_AMT,
          MULTIPLIER: val.MULTIPLIER,
          PROJECT_ID: val.PROJECT_ID,
          PROJECT_STEP_ID: val.PROJECT_STEP_ID,
          AUTO_REVERSE_YN: val.AUTO_REVERSE_YN,
          SCHD_H_LINE_NO: val.SCHD_H_LINE_NO,
          TP_KEY: val.TP_KEY,
          TRANS_DETAILS_KEY: val.TRANS_DETAILS_KEY,
          POSTING_GL_ACCT_KEY: ''
        });
      });
    }

    const headerSettings = [headerObj];
    if (transactions.length === 0) {
      transactions.push(headerObj);
    }

    const message = isEdit
      ? 'E&P Adjustments have been successfully updated'
      : 'E&P Adjustments have been successfully created';

    const scenario = this.filterParams.scenario || this.globalService?.globalParams?.scenario;
    const extraParams: any = {
      checkLockedbj: [{
        leid: this.rowData.LEID || this.rowData.SOURCE_LEID || '',
        me_code: this.rowData.ME_CODE || this.rowData.ME || this.rowData.SOURCE_ME_CODE || '',
        reporting_period: this.rowData.REPORTING_PERIOD || this.rowData.SOURCE_REPORTING_PERIOD || '',
        combination_key: this.rowData.COMBINATION_KEY || '',
        scenario_key: this.rowData.SCENARIO_KEY || scenario || ''
      }],
      scenarioCode: this.globalService?.globalParams?.scenarioCode || '',
      is_issue_key_enabled: this.globalService?.globalParams?.is_issue_key_enabled || 'N'
    };

    this.epAdjsService.saveUpdateAdjustments(
      this.GENERAL_CONFIG.base_url,
      headerSettings,
      transactions,
      this.globalService,
      this.filterParams,
      extraParams
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open(message, 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  private saveDCS(): void {
    const isEdit = this.isEdit;

    // Resolve keys from lookup data
    const templateData = this.mainModalData.find(
      (item: any) => item.SCHDM_TEMPLATE === this.template
    );
    const newTemplateKey = templateData?.SCHDM_TEMPLATE_ID || this.templateKey || '';

    const schdMData = this.schdMTypeData.find(
      (item: any) => item.SCHDM_TYPE === this.schdMt
    );
    const newSchdMKey = schdMData?.SCHDM_TYPE_SAVE || '';

    // Legacy sends SCHDM_TYPE_ID directly as OLD_schdm_Type (vm.oldschdm = existingAdjustments[i].SCHDM_TYPE_ID).
    const oldSchdMKey = isEdit ? (this.oldSchdMId || newSchdMKey) : newSchdMKey;

    const tbbsData = this.tbbsAcctData.find(
      (item: any) => item.TAX_STREAM_ACCT === this.tbbsAcctVal
    );
    const newTbbsAcctKey = tbbsData?.TAX_STREAM_ACCT_SAVE || '';

    const projectId = this.parentObject.PROJECT_ID || '';
    const projectStepId = this.parentObject.PROJECT_STEP_ID || '';

    // New Posting Acct variant (testFlag): post against the selected posting account
    // instead of the row's local account (legacy 888-896 / 957-958).
    const localAcctKey = this.testFlag
      ? this.resolvePostingAcctKey(this.postingTaxTop, this.glAcctsMData)
      : this.rowData.LOCAL_ACCT_KEY;

    const headerObj: any = {
      TYPE: isEdit ? 'E' : 'I',
      REF_KEY: isEdit ? this.refKey : 0,
      TAX_YEAR: this.filterParams.tax_year || this.globalService?.globalParams?.tax_year,
      SCENARIO: this.filterParams.scenario || this.globalService?.globalParams?.scenario,
      JCD_KEY: this.globalService?.globalParams?.jcd_key,
      OLD_Template: this.template,
      NEW_Template: this.template,
      OLD_TEMPLATE_ID: isEdit ? (this.templateKey || newTemplateKey) : newTemplateKey,
      NEW_TEMPLATE_ID: newTemplateKey,
      NEW_USER_M1_DESCRIPTION: this.userM1,
      OLD_USER_M1_DESCRIPTION: this.userM1,
      OLD_schdm_Type: oldSchdMKey,
      NEW_SCHDM_TYPE: newSchdMKey,
      OLD_TAX_BALANCESHEET_ACCT: newTbbsAcctKey,
      NEW_TAX_BALANCESHEET_ACCT: newTbbsAcctKey,
      OLD_PROVISON_M1: this.provision === 'Yes' ? 'Y' : 'N',
      NEW_PROVISON_M1: this.provision === 'Yes' ? 'Y' : 'N',
      NEW_PROJECT_NAME: projectId,
      OLD_PROJECT_NAME: projectId,
      NEW_PROJECT_STEP_CODE: projectStepId,
      OLD_PROJECT_STEP_CODE: projectStepId,
      NEW_COMMENTS: this.COMMENTS || '',
      OLD_COMMENTS: isEdit ? this.oldDcsComments : '',
      COMMENTS: this.COMMENTS || '',
      NEW_CY_SCHD_M1_AMOUNT: this.CURRENT_YR_AMT,
      OLD_CY_SCHD_M1_AMOUNT: this.CURRENT_YR_AMT,
      SOURCE_SYSTEM_FLAG: 'D',
      CHART_KEY: this.rowData.LOCAL_CHART_KEY,
      LOCAL_ACCT_KEY: localAcctKey,
      COMBINATION_KEY: this.rowData.COMBINATION_KEY,
      SCHDM_TRANS_HEADER_KEY: this.rowData.TRANS_HEADER_KEY,
      SCHDM_TRANS_DETAILS_KEY: this.rowData.TRANS_DETAILS_KEY,
      OLD_COL_2_AMT: this.custom2Amt || 0,
      NEW_COL_2_AMT: this.custom2Amt || 0,
      SCHDM_ID: isEdit ? (this.dcsSchdmId || 0) : 0,
      TP_KEY: this.tpObjectD?.TP_KEY || 0,
      OLD_SETTLED_FLAG: this.settledYesNo === 'Yes' ? 'Y' : 'N',
      OLD_RECURRING_FLAG: this.recurring ? 'Y' : 'N',
      OLD_BA_EXIST: this.bookAmtExist ? 'Y' : 'N',
      OLD_REPO_TRANS: this.rptTFlag === 'Yes' ? 'Y' : 'N',
      OLD_REVIEWED: this.sch === 'Yes' ? 'Y' : 'N',
      NEW_SETTLED_FLAG: this.settledYesNo === 'Yes' ? 'Y' : 'N',
      NEW_RECURRING_FLAG: this.recurring ? 'Y' : 'N',
      NEW_REVIEWED: this.sch === 'Yes' ? 'Y' : 'N',
      OLD_USER_REVIEWED: this.userReviewd,
      NEW_USER_REVIEWED: this.userReviewd,
      OLD_REVIEWED_ON: this.timestamp || new Date().toISOString(),
      NEW_REVIEWED_ON: this.timestamp || new Date().toISOString(),
      NEW_BA_EXIST: this.bookAmtExist ? 'Y' : 'N',
      NEW_REPO_TRANS: this.rptTFlag === 'Yes' ? 'Y' : 'N'
    };

    const transactions = [headerObj];
    // Reportable Transaction details go into the settings payload (legacy 1018-1159).
    const tempSettings: any[] = this.rptTFlag === 'Yes' ? this.buildReportableSettings() : [];

    const message = isEdit
      ? 'Tax Adjustments have been successfully updated'
      : 'Tax Adjustments have been successfully created';

    const scenario = this.filterParams.scenario || this.globalService?.globalParams?.scenario;
    const extraParams: any = {
      checkLockedbj: [{
        leid: this.rowData.LEID || this.rowData.SOURCE_LEID || '',
        me_code: this.rowData.ME_CODE || this.rowData.ME || this.rowData.SOURCE_ME_CODE || '',
        reporting_period: this.rowData.REPORTING_PERIOD || this.rowData.SOURCE_REPORTING_PERIOD || '',
        combination_key: this.rowData.COMBINATION_KEY || this.rowData.COMPL_COMBINATION_KEY || this.rowData.HO_COMBINATION_KEY || '',
        scenario_key: this.rowData.SCENARIO_KEY || scenario || ''
      }],
      scenarioCode: this.globalService?.globalParams?.scenarioCode || '',
      is_issue_key_enabled: this.globalService?.globalParams?.is_issue_key_enabled || 'N'
    };

    this.epAdjsService.saveUpdateAdjustmentsDCS(
      this.GENERAL_CONFIG.base_url,
      tempSettings,
      transactions,
      this.globalService,
      this.filterParams,
      extraParams
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000, panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open(message, 'Close', {
            duration: 4000, panelClass: ['success-snackbar']
          });
          this.loadExistingAdjustments();
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000, panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  // DCS Book Reportable save
  save1(): void {
    if (this.crudLoadingBook) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    // Legacy checkData(): re-run all book validations before saving.
    this.rptDescChangeBook();
    this.typeOfRptTransChangeBook();
    this.adjustedAmtChangeBook();
    this.glacctErrorBook = this.reportableListBook.some(r => !r.glacct_book);

    if (this.rptDescErrorBook || this.typeOfRptErrorBook || this.glacctErrorBook || this.thisAdjAmtErrorBook) {
      this._snackBar.open('Please correct the errors below', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      this.crudLoadingBook = false;
      this.cdr.detectChanges();
      return;
    }

    this.crudLoadingBook = true;
    this.cdr.detectChanges();

    const gp = this.globalService?.globalParams || {};
    const newTypeCode = this.resolveTypeReportableBookCode(this.typeOfReportableTransBook);
    // New Posting Acct variant (testFlag) posts to the selected posting account (legacy 4138-4149).
    const rowLocalAcctKey = this.testFlag
      ? this.resolvePostingAcctKey(this.postingTaxTopB, this.glAcctsMDataB)
      : this.rowData.LOCAL_ACCT_KEY;

    const tempSettingsBook: any[] = [];

    // One entry per book reportable G/L row (legacy tempobj_book).
    for (const row of this.reportableListBook) {
      const tempobjBook: any = {
        TYPE: this.typeBook ? 'E' : 'I',
        REF_KEY: this.typeBook ? this.refKey : 0,
        TAX_YEAR: gp.tax_year,
        SCENARIO: gp.scenario,
        JCD_KEY: gp.jcd_key,
        CHART_KEY: this.rowData.LOCAL_CHART_KEY,
        LOCAL_ACCT_KEY: rowLocalAcctKey,
        COMBINATION_KEY: this.rowData.COMBINATION_KEY
      };

      if (this.typeBook) {
        tempobjBook.OLD_TAX_SHELTER_REGISTRATION_NUMBER = this.oldTaxShelterRegNumBook;
        tempobjBook.OLD_REPORTABLE_TRANSACTION_DESCRIPTION = this.oldReportableTransDescBook;
        tempobjBook.OLD_TYPE_REPORTABLE_TRANSACTION = this.oldTypeOfReportableTransBook;
        if (row.type === 'I') {
          tempobjBook.OLD_GL_ACCT = this.resolveGlAcctKey(row.glacct_book);
          tempobjBook.OLD_THIS_ADJ_AMT = row.thisadj_amt_book;
        } else {
          tempobjBook.OLD_GL_ACCT = row.old_gl_acct_key;
          tempobjBook.OLD_THIS_ADJ_AMT = row.old_thisadj_amt;
        }
      } else {
        tempobjBook.OLD_TAX_SHELTER_REGISTRATION_NUMBER = this.taxShelterRegNumBook;
        tempobjBook.OLD_REPORTABLE_TRANSACTION_DESCRIPTION = this.reportableTransDescBook;
        tempobjBook.OLD_TYPE_REPORTABLE_TRANSACTION = newTypeCode;
        if (!this.testFlag) {
          tempobjBook.OLD_GL_ACCT = this.resolveGlAcctKey(row.glacct_book);
          tempobjBook.OLD_THIS_ADJ_AMT = row.thisadj_amt_book;
        }
      }

      tempobjBook.NEW_TAX_SHELTER_REGISTRATION_NUMBER = this.taxShelterRegNumBook;
      tempobjBook.NEW_REPORTABLE_TRANSACTION_DESCRIPTION = this.reportableTransDescBook;
      tempobjBook.NEW_TYPE_REPORTABLE_TRANSACTION = newTypeCode;
      tempobjBook.NEW_GL_ACCT = this.resolveGlAcctKey(row.glacct_book);
      tempobjBook.NEW_THIS_ADJ_AMT = row.thisadj_amt_book;

      tempSettingsBook.push(tempobjBook);
    }

    // Static summary entry (legacy tempobjStatic_book).
    const rtAcctKey = this.glacctsRpt && this.glacctsRpt.length > 0 ? this.glacctsRpt[0].RT_ACCT_KEY : undefined;
    const tempobjStaticBook: any = {
      OLD_TAX_SHELTER_REGISTRATION_NUMBER: this.typeBook ? this.oldTaxShelterRegNumBook : this.taxShelterRegNumBook,
      OLD_REPORTABLE_TRANSACTION_DESCRIPTION: this.typeBook ? this.oldReportableTransDescBook : this.reportableTransDescBook,
      OLD_TYPE_REPORTABLE_TRANSACTION: this.typeBook ? this.oldTypeOfReportableTransBook : newTypeCode,
      NEW_TAX_SHELTER_REGISTRATION_NUMBER: this.taxShelterRegNumBook,
      NEW_REPORTABLE_TRANSACTION_DESCRIPTION: this.reportableTransDescBook,
      NEW_TYPE_REPORTABLE_TRANSACTION: newTypeCode,
      OLD_GL_ACCT: rtAcctKey,
      NEW_GL_ACCT: rtAcctKey,
      OLD_THIS_ADJ_AMT: this.thisAdjAmt1Book,
      NEW_THIS_ADJ_AMT: this.thisAdjAmt1Book,
      REF_KEY: this.typeBook ? this.refKey : 0,
      TAX_YEAR: gp.tax_year,
      SCENARIO: gp.scenario,
      JCD_KEY: gp.jcd_key,
      CHART_KEY: this.rowData.LOCAL_CHART_KEY,
      LOCAL_ACCT_KEY: this.rowData.LOCAL_ACCT_KEY,
      COMBINATION_KEY: this.rowData.COMBINATION_KEY
    };
    tempSettingsBook.push(tempobjStaticBook);

    // Legacy passes tempSettings_book as BOTH jsonObj and jsonSettings.
    this.epAdjsService.saveUpdateAdjustmentsDCSBookReportable(
      this.GENERAL_CONFIG.base_url,
      tempSettingsBook,
      tempSettingsBook,
      this.globalService,
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (response: any) => {
        this.crudLoadingBook = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Book Reportable adjustment saved successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoadingBook = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  // DCS Book Reportable reset
  reset1(): void {
    this.reportableTransDescBook = '';
    this.taxShelterRegNumBook = '';
    this.typeOfReportableTransBook = '';
    this.reportableListBook = [];
    this.glacct1Book = '';
    this.cyAmt1Book = null;
    this.earlierAdjAmt1Book = null;
    this.thisAdjAmt1Book = null;
    this.adjCyAmt1Book = null;
    this.totalThisAdjAmtStaticBook = null;
    this.totalThisAdjAmtBook = 0;
    this.typeBook = false;
    this.refKey = 0;
    this.pendingBookReportableType = null;
    this.glacctErrorBook = false;
    this.thisAdjAmtErrorBook = false;
    this.oldReportableTransDescBook = '';
    this.oldTaxShelterRegNumBook = '';
    this.oldTypeOfReportableTransBook = '';
    this.cdr.detectChanges();
  }

  // DCS Book Reportable cancel
  cancel1(): void {
    this.requestGridUpdate.emit('cancel');
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  trackByIndex(index: number): number {
    return index;
  }

  // Format number with thousands separator
  formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '0';
    }
    return value.toLocaleString('en-US');
  }
}
